/**
 * ArchLang Resolver (v0.2)
 *
 * Takes a `ParsedFile` and produces a `ResolvedModel` plus a stream of
 * diagnostics. Two concerns:
 *
 *   1. Kind registry: collect all `type` declarations, link parent chains,
 *      and compute each kind's "stamped body" — the merged template that
 *      gets applied to every instance of that kind.
 *
 *   2. Instance resolution: for each top-level module/process/view, apply
 *      its kind's stamped body, then layer in instance refinements
 *      (refine/override/drop). Recurse through sub-declarations
 *      (surfaces, interfaces, child modules).
 *
 * Cascade and structural-cascade resolution (mechanism B in the spec) is
 * *not* performed here — those are query-time concerns. The resolver
 * leaves cascade-modified declarations in the resolved bodies so a
 * downstream lookup can walk the structural tree.
 */

import type {
  Body,
  CascadeDrop,
  Diagnostic,
  ExtensionDecl,
  DropStmt,
  SurfaceDecl,
  FieldDecl,
  BlockStep,
  InterfaceBody,
  InterfaceDecl,
  AspectEntry,
  ModuleDecl,
  Name,
  OnInclusion,
  ParsedFile,
  ProcessDecl,
  ProcessStep,
  QualifiedName,
  SourceSpan,
  StableId,
  StepOwner,
  SubprocessDecl,
  TopLevelDecl,
  TypeDecl,
  Value,
} from "./types.js";
import { EMPTY_BODY, EMPTY_INTERFACE_BODY } from "./types.js";
import { makeDiag } from "./diagnostics/catalog.js";
import type { DiagnosticCode } from "./diagnostics/codes.js";
import type { LensDecl, PolicyDecl, TestDecl, ViewDeclV2 } from "./v010-ast.js";
import { buildDecorationRegistry } from "./decorations.js";
import type { DecorationDecl, DecorationRegistry } from "./decorations.js";
import { collectModelMarkers } from "./decoration-validate.js";
import { validateViewInstances } from "./view-projection.js";
import { buildTimeline, EMPTY_TIMELINE, type Timeline } from "./timeline.js";
import { STDLIB_INTERFACE_KINDS, STDLIB_KIND_PACKAGES } from "./generated/stdlib-kinds.js";
import type { MarkdownDocument } from "./markdown/document.js";
import type { ProcessModel, SubprocessModel } from "./process-model.js";
import { resolveFieldExprs } from "./field-exprs.js";
import { buildProcessModels, type ProcessModelCache } from "./process-model-builder.js";
import { computePresence, type PresenceInfo } from "./presence.js";
import { spacedKey } from "./space-identity.js";

// ---------------------------------------------------------------------------
// Incremental resolve (B3). `resolveModule(decl, registry, space)` resolves a
// module in ISOLATION — it reads only its own declaration AST + the type
// registry (cross-module linking happens in later light passes). So its output
// is a pure function of (decl, registry, space). The parse cache (B2) hands
// back the SAME `decl` object for an unchanged file, so we memoise per decl
// (a WeakMap, auto-freed when the AST is evicted) keyed by a content fingerprint
// of the registry + the declaring space. An edit re-resolves only the modules
// in the changed file; a type edit changes the registry fingerprint and
// re-resolves everything (correct — types stamp every instance). Diagnostics
// are captured + replayed so a cache hit reproduces them exactly.
interface CachedResolve {
  readonly resolved: ResolvedModule;
  readonly diagnostics: readonly Diagnostic[];
}
const resolveModuleCache = new WeakMap<ModuleDecl, Map<string, CachedResolve>>();
let resolveHits = 0;
let resolveMisses = 0;

/** Reset the incremental resolve cache + counters (tests; self-bounds via the
 *  WeakMap otherwise). */
export function _resetResolveCache(): void {
  // WeakMap can't be cleared wholesale; drop the counters and let GC reclaim
  // entries as ASTs are released. Tests that need a true cold run also reset
  // the parse cache, which releases the decl keys.
  resolveHits = 0;
  resolveMisses = 0;
}

/** Cache hit/miss counters since the last reset (cost assertions). */
export function _resolveCacheStats(): { hits: number; misses: number } {
  return { hits: resolveHits, misses: resolveMisses };
}

/** FNV-1a over a string — fast, non-crypto, sufficient for a cache key. */
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(36);
}

function resolveModuleMemo(
  decl: ModuleDecl,
  registry: ResolvedTypeRegistry,
  registryKey: string,
  diagnostics: Diagnostic[],
  space?: QualifiedName,
): ResolvedModule {
  const innerKey = `${registryKey} ${space ? space.join(".") : ""}`;
  let perDecl = resolveModuleCache.get(decl);
  const hit = perDecl?.get(innerKey);
  if (hit) {
    resolveHits++;
    diagnostics.push(...hit.diagnostics);
    return hit.resolved;
  }
  resolveMisses++;
  const localDiags: Diagnostic[] = [];
  const resolved = resolveModule(decl, registry, localDiags, space);
  if (!perDecl) {
    perDecl = new Map();
    resolveModuleCache.set(decl, perDecl);
  }
  perDecl.set(innerKey, { resolved, diagnostics: localDiags });
  diagnostics.push(...localDiags);
  return resolved;
}

/**
 * Builtin defaults for the `module` base kind. Shaped exactly like a
 * parsed type body — a cascade-group root `widget: arch-module` with
 * `icon` and `color` sub-fields plus plain `widget.width` /
 * `widget.height` size leaves. Stamped into every module instance via
 * the same `mergeBodies` path that subtype templates use, so subtypes
 * (microservice, frontend, …) and instance overrides win normally,
 * `widget: <other-tag>` cleanly drops the inherited group, and
 * downstream renderers read `widget.*` props with no name-based
 * branching.
 *
 * Lives in the language implementation but the data is archlang-shape;
 * adding new defaults means appending FieldDecls here, not editing
 * renderer code.
 */
const BUILTIN_MODULE_BODY: Body = {
  ...EMPTY_BODY,
  fields: [
    {
      path: ["widget"],
      value: { kind: "identifier", text: "arch-module" },
      cascade: true,
      group: true,
      fromTemplate: true,
    },
    {
      path: ["widget", "icon"],
      value: { kind: "identifier", text: "module" },
      cascade: true,
      fromTemplate: true,
    },
    {
      // A palette ROLE, not a colour: the engine names the meaning and each
      // backend maps the name to pixels (`@archlang/scene`'s `paletteColor`).
      // Core has no business knowing a hex — and a CSS var here reached the
      // exported SVG verbatim, where resvg resolves nothing.
      path: ["widget", "color"],
      value: { kind: "identifier", text: "info" },
      cascade: true,
      fromTemplate: true,
    },
    {
      path: ["widget", "width"],
      value: { kind: "number", value: 220 },
      fromTemplate: true,
    },
    {
      path: ["widget", "height"],
      value: { kind: "number", value: 140 },
      fromTemplate: true,
    },
  ],
};

// =============================================================================
// Public types
// =============================================================================

export type BaseType = "module" | "surface" | "interface" | "process" | "view";

const BASE_KINDS: ReadonlySet<string> = new Set([
  "module",
  "surface",
  "interface",
  "process",
  "view",
]);

// Pre-seeded stdlib interface kinds (generated from packages/stdlib) — a
// fallback so a project that uses these kind names without an explicit
// `type interface X` declaration still resolves. A project's own
// `type interface X { ... }` always wins over this fallback.

export interface ResolvedType {
  readonly name: string;
  readonly baseType: BaseType;
  /** Parent chain from immediate parent up to (but excluding) the base. */
  readonly chain: readonly string[];
  /**
   * The stamped body — merged content of this kind plus all ancestors.
   * Applied to every instance of this kind.
   */
  readonly stampedBody: Body;
  readonly decl?: TypeDecl;
  readonly source?: SourceSpan;
  /** Declaring space (v0.5). Undefined = package root (package-global). */
  readonly space?: QualifiedName;
}

export interface ResolvedTypeRegistry {
  /**
   * Name → resolved type. For backward compatibility this is the primary,
   * name-keyed view (root/first declaration wins on a cross-space name
   * collision). Consumers that don't care about spaces use this unchanged.
   */
  readonly kinds: ReadonlyMap<string, ResolvedType>;
  /**
   * Qualified id (`<space.path>::<name>`, root = `::<name>`) → resolved type
   * (v0.5). Holds every declaration, including same-named types in different
   * spaces. Use {@link lookupKind} for nearest-first resolution from a space.
   */
  readonly byId: ReadonlyMap<string, ResolvedType>;
  /**
   * Name → full ancestor chain, computed for EVERY declaration before body
   * stamping (chains need only parent links, never bodies). Subtype checks
   * that run while `kinds` is still being filled (template stamping in
   * `buildRegistry`) consult this oracle so recognition never depends on
   * declaration order — imported synthetics are appended after local
   * declarations, and a local family may be declared below its consumer.
   */
  readonly chainOracle?: ReadonlyMap<string, readonly string[]>;
}

// =============================================================================
// Space-aware kind lookup (v0.5)
// =============================================================================

function spaceKey(space: QualifiedName | undefined): string {
  return space && space.length > 0 ? space.join(".") : "";
}

function kindId(space: QualifiedName | undefined, name: string): string {
  return spaceKey(space) + "::" + name;
}

/** Spaces to try for nearest-first resolution: self, ancestors, then root. */
export function spaceChain(
  space: QualifiedName | undefined
): readonly (QualifiedName | undefined)[] {
  if (!space || space.length === 0) return [undefined];
  const out: (QualifiedName | undefined)[] = [];
  for (let i = space.length; i >= 1; i--) out.push(space.slice(0, i));
  out.push(undefined); // package root
  return out;
}

function lookupIn(
  byId: ReadonlyMap<string, ResolvedType>,
  name: string,
  fromSpace: QualifiedName | undefined
): ResolvedType | undefined {
  for (const s of spaceChain(fromSpace)) {
    const t = byId.get(kindId(s, name));
    if (t) return t;
  }
  return undefined;
}

/**
 * Resolve a bare kind name from a given space, nearest-first (same space →
 * ancestor spaces → package root), falling back to the name-keyed view (base
 * kinds and imported types live at root). When `fromSpace` is undefined this
 * is exactly the old `registry.kinds.get(name)` behavior.
 */
export function lookupKind(
  registry: ResolvedTypeRegistry,
  name: string,
  fromSpace: QualifiedName | undefined
): ResolvedType | undefined {
  return lookupIn(registry.byId, name, fromSpace) ?? registry.kinds.get(name);
}

/**
 * Resolve a (possibly qualified) parent type name in a `type Parent Name`
 * declaration (v0.5). A bare name resolves nearest-first. A qualified
 * `Space.Path.Type` splits its trailing segment as the type name and the
 * leading segments as a space path, tried first as an absolute path from the
 * package root, then relative to the declaring space.
 */
function resolveParentType(
  byId: ReadonlyMap<string, ResolvedType>,
  parentName: string,
  fromSpace: QualifiedName | undefined
): ResolvedType | undefined {
  if (!parentName.includes(".")) return lookupIn(byId, parentName, fromSpace);
  const parts = parentName.split(".");
  const typeName = parts[parts.length - 1]!;
  const spacePath = parts.slice(0, -1);
  return (
    byId.get(kindId(spacePath, typeName)) ??
    (fromSpace
      ? byId.get(kindId([...fromSpace, ...spacePath], typeName))
      : undefined)
  );
}

export interface ResolvedModule {
  readonly decl: ModuleDecl;
  readonly name: string;
  readonly typeName: string;
  readonly baseType: BaseType;
  readonly id?: StableId;
  readonly parentPath?: readonly Name[];
  /** Body after template stamping + instance refinement/override/drop. */
  readonly body: Body;
  readonly surfaces: readonly ResolvedSurface[];
  readonly interfaces: readonly ResolvedInterface[];
  readonly modules: readonly ResolvedModule[];
  /**
   * True when this module was **synthesized as a stub** (v0.6, §4.13) because a
   * process referenced it before it was defined. Not authored — a draft
   * placeholder. Renderers draw it dashed; the incompleteness is reported as a
   * `todo` diagnostic. Cleared once the author defines the module explicitly.
   */
  readonly synthesized?: boolean;
  /** True when the name was omitted and derived from the type (§4.14). No
   *  `#id` until promoted; single-use per parent; modules only. */
  readonly unnamed?: boolean;
  /**
   * v0.11 (D1a): the module's LANDED identity space. Stamped from the
   * declaring file at resolution; re-stamped to the placement target's
   * space when an `in <Parent>` orphan lands in another space's subtree
   * (`applyInClauses`). Undefined at package root / spaceless workspaces.
   * Body-nested children share the file (nesting is lexical), so the whole
   * landed subtree carries one space.
   */
  readonly space?: QualifiedName;
}

export interface ResolvedSurface {
  readonly decl: SurfaceDecl;
  readonly name: string;
  readonly typeName: string;
  readonly body: Body;
  readonly surfaces: readonly ResolvedSurface[];
  readonly interfaces: readonly ResolvedInterface[];
}

export interface ResolvedInterface {
  readonly decl: InterfaceDecl;
  readonly name: string;
  readonly typeName: string;
  readonly body: InterfaceBody;
  /** True when synthesized as a stub (v0.6, §4.13) — a process named it as a
   *  callee before it was defined. See {@link ResolvedModule.synthesized}. */
  readonly synthesized?: boolean;
  /** True when this is the synthesized `(anonymous)` handler that stands in
   *  for an unspecified callee interface (§4.13.2 / §10.3). Always also
   *  `synthesized`. Rendered as "(unspecified)"; counts as a TODO. */
  readonly anonymous?: boolean;
}

export interface ResolvedModel {
  readonly types: readonly TypeDecl[];
  readonly registry: ResolvedTypeRegistry;
  readonly modules: readonly ResolvedModule[];
  readonly processes: readonly ProcessDecl[];
  readonly subprocesses: readonly SubprocessDecl[];
  readonly views: readonly ViewDeclV2[];
  /**
   * v0.10 §3 / VIEWS.md §8: top-level `lens { … }` declarations — inspector-wide
   * in their declaring space's scope. Module-level lens blocks stay on
   * `Body.lenses`; a view's `lens` clause lives on the view. `computeLensEntries`
   * (lens-eval.ts) merges the three layers per inspected element.
   */
  readonly lenses?: readonly LensDecl[];
  /**
   * Process-tests P0/P7: top-level + `in`-placed `test "…" of P { … }`
   * declarations, hoisted onto the model (module-body tests stay on
   * `Body.tests`). `runTests` (process-test-run.ts) gathers both layers.
   */
  readonly tests?: readonly TestDecl[];
  /** User-authored `decoration Name { … }` declarations. */
  readonly decorationDecls?: readonly DecorationDecl[];
  /** Every decoration in scope, with `planned` / `deprecated` seeded. Always
   *  present on a resolved model, so consumers need no fallback. */
  readonly decorationRegistry?: DecorationRegistry;
  /**
   * The timeline every lifecycle marker in this model orders on — DERIVED from
   * the markers themselves, never declared. Always present (empty when nothing
   * carries a lifecycle decoration), so consumers need no fallback.
   */
  readonly timeline?: Timeline;
  /**
   * The resolved per-process IR (v0.8) — one {@link ProcessModel} per process in
   * {@link processes}, built by `process-model-builder.ts`. The canonical
   * artifact every consumer is migrating onto (graph/validation/stubs/render/
   * LSP read *this*, not the raw `ProcessStep` AST). Owners, callee
   * classification, chain expansion, `unwind` pairing, and `go`/`do` targets are
   * resolved here once. Carried through `applyCascade` unchanged (cascade only
   * re-stamps module fields). Undefined only on a hand-built bare model.
   *
   * Phase 2 builds + caches these; Phase 3 migrates the AST consumers onto them.
   */
  /** v0.9.0 §1.1: planar presence — plane sets per module, plane list, and
   *  membership instance names per place. Undefined on hand-built models. */
  readonly presence?: import("./presence.js").PresenceInfo;
  readonly processModels?: readonly ProcessModel[];
  /** The resolved per-subprocess IR — one {@link SubprocessModel} per entry in
   *  {@link subprocesses} plus each process-nested subprocess. */
  readonly subprocessModels?: readonly SubprocessModel[];
  /**
   * File URI → enclosing space (v0.5). Carried on the model so consumers
   * (validation, description index, render) can resolve names nearest-first
   * by space without separate plumbing. Undefined/empty for single-space
   * packages — behavior is then identical to pre-v0.5.
   */
  readonly spaces?: ReadonlyMap<string, QualifiedName>;
  /**
   * Markdown documents in the archspace (§4.15). A `.md` declares no canonical
   * elements — it carries flavored-markdown prose + cross-references. Populated
   * by the package loader (discovery is viewer-core's job); `undefined`/empty
   * for a plain single-file resolve. Passed through `applyCascade` unchanged.
   */
  readonly documents?: readonly MarkdownDocument[];
  /**
   * v0.9.0 §1.2: top-level `on <plane> { … }` inclusion blocks — each names a
   * module (declared elsewhere) to add to a plane. Fed to `computePresence` and
   * to `validateInclusions` (an unresolved target is a `TODO`). Carried through
   * `applyCascade` unchanged; undefined on hand-built models.
   */
  readonly onInclusions?: readonly OnInclusion[];
}

export interface ResolutionResult {
  readonly model: ResolvedModel;
  readonly diagnostics: readonly Diagnostic[];
}

// =============================================================================
// Top-level entry point
// =============================================================================

export function resolveFile(
  file: ParsedFile,
  spaces?: ReadonlyMap<string, QualifiedName>,
  /** Wall-clock instant `now` is placed against on the derived timeline —
   *  injectable so goldens do not drift with the calendar. */
  nowMs?: number,
): ResolutionResult {
  const diagnostics: Diagnostic[] = [];
  // Map a declaration to its enclosing space via its source file (v0.5).
  // No `spaces` → every declaration is root, and resolution is byte-identical
  // to the pre-v0.5 name-keyed path.
  const spaceOf = (d: { source?: SourceSpan }): QualifiedName | undefined =>
    spaces && d.source?.file ? spaces.get(d.source.file) : undefined;

  // 1. Collect type declarations and check for collisions.
  const types: TypeDecl[] = [];
  const modules: ModuleDecl[] = [];
  const processes: ProcessDecl[] = [];
  const subprocesses: SubprocessDecl[] = [];
  const views: ViewDeclV2[] = [];
  const lenses: LensDecl[] = [];
  const tests: TestDecl[] = [];
  const extensions: ExtensionDecl[] = [];
  const decorationDecls: DecorationDecl[] = [];

  for (const top of file.declarations) {
    switch (top.kind) {
      case "type":
        types.push(top.decl);
        break;
      case "module":
        modules.push(top.decl);
        break;
      case "process":
        processes.push(top.decl);
        break;
      case "subprocess":
        subprocesses.push(top.decl);
        break;
      case "view":
        views.push(top.decl);
        break;
      case "lens":
        lenses.push(top.decl);
        break;
      case "test":
        tests.push(top.decl);
        break;
      case "extension":
        extensions.push(top.decl);
        break;
      case "decoration":
        decorationDecls.push(top.decl);
        break;
    }
  }

  // v0.8.2 §4.16: merge each `in Target { … }` extension block into its target
  // module's body (set union) before resolution, so merged members resolve as
  // if written inside the target. A missing target is a hard IN_TARGET_NOT_FOUND.
  const effectiveModules = extensions.length
    ? applyExtensionBlocks(modules, extensions, diagnostics)
    : modules;

  // 2. Build the kind registry (space-aware).
  const registry = buildRegistry(types, diagnostics, spaceOf);

  // 2b. Decoration registry — same space-aware dual-map shape, with the
  //     engine-bound lifecycle built-ins seeded in.
  const decorationRegistry = buildDecorationRegistry(decorationDecls, {
    spaceOf,
    onDuplicate: (d, prev) =>
      diagnostics.push(
        makeDiag(
          "error",
          d.nameSource ?? d.source,
          prev.builtin ? "DECORATION_REDECLARES_BUILTIN" : "DUPLICATE_DECORATION",
          prev.builtin ? "resolver.decorationRedeclaresBuiltin" : "resolver.duplicateDecoration",
          { name: d.name },
        ),
      ),
  });

  // 3. Resolve each top-level module instance in its declaring space.
  //    Memoised per decl (B3): `registryKey` is a content fingerprint of the
  //    type set — a module's resolution depends only on its own AST + this
  //    registry + its space, so an edit re-resolves only the changed file's
  //    modules. The key is content-based (not identity) because imported types
  //    are synthesised fresh each call; same source ⇒ same key ⇒ cache hits.
  const registryKey = fnv1a(JSON.stringify(types));
  const resolvedModules = effectiveModules.map((m) =>
    resolveModuleMemo(m, registry, registryKey, diagnostics, spaceOf(m))
  );

  // 4-6. Assemble the tree (in-clause re-parenting), scope processes &
  //      subprocesses. Shared with the incremental compiler so both paths
  //      produce byte-identical models.
  const model = assembleModel(
    resolvedModules,
    {
      types,
      registry,
      processes,
      subprocesses,
      views,
      lenses,
      tests,
      decorationDecls,
      decorationRegistry,
      nowMs,
      spaces,
      onInclusions: file.onInclusions,
    },
    diagnostics
  );

  // Planar presence (§1.1/§1.2) is attached inside `assembleModel` — shared by
  // the cold and incremental paths so the IR's instance-actor resolution and
  // every presence-derived diagnostic match on both.
  return { model, diagnostics };
}

/**
 * Deferred drop validation (§4.9): a `aspects { drop X }` whose aspect was not in
 * the template is legal when a structural ancestor supplies it via cascade, and
 * an error only when it is genuinely uninherited. This needs the structural
 * tree (unavailable during per-body merge), so it runs after the model is
 * assembled. Field drops are validated at merge time (cascade fields always
 * stamp a blank, so a missing one is caught there).
 */
/**
 * Root-scope duplicate-name guard. Two top-level modules sharing a name are
 * a conflict (each carries its own identity/body, so references can only bind
 * to one). Reports every declaration after the first. Mirrors the per-body
 * one-op-per-target rule for the root scope, which is not itself a body.
 */
function validateRootModuleUniqueness(
  roots: readonly ResolvedModule[],
  diagnostics: Diagnostic[],
): void {
  const seen = new Set<string>();
  for (const m of roots) {
    // v0.11: root-name uniqueness is per landed space (§4.11 — "scoped
    // resolution keeps names from clashing"); spaceless keys stay bare.
    const key = spacedKey(m.space, m.name);
    if (seen.has(key)) {
      diagnostics.push(
        makeDiag(
          "error",
          m.decl.nameSource ?? m.decl.source,
          "DUPLICATE_MODULE",
          "resolver.duplicateModule",
          // `parent` carries the literal `<Parent>` placeholder as a param: the
          // catalog renderer feeds templates to ICU, which parses a bare
          // `<Parent>` as an (unclosed) XML tag and throws — so the angle-bracket
          // literal rides in as a value. Not translatable prose.
          { name: m.name, parent: "<Parent>" },
        ),
      );
    } else {
      seen.add(key);
    }
  }
}

/**
 * Name-uniqueness guard for the flat top-level declaration lists that have no
 * enclosing body to run the per-body one-op-per-target check: processes, views
 * and subprocesses. Each is keyed by its scope so the same short name under
 * different owners stays legal (e.g. a `Checkout` process inside two different
 * modules), but a true collision — two declarations that resolve to the same
 * qualified name / lookup key — is reported. Without this, the second silently
 * shadows the first: `do X` binds to one body, navigation/playback picks one,
 * the other floats. Mirrors the module + type duplicate guards.
 */
function validateUniqueDeclarations(
  processes: readonly ProcessDecl[],
  subprocesses: readonly SubprocessDecl[],
  views: readonly ViewDeclV2[],
  diagnostics: Diagnostic[],
): void {
  const report = (
    name: string,
    kind: string,
    code: DiagnosticCode,
    source: SourceSpan | undefined,
  ) =>
    diagnostics.push(
      makeDiag(
        "error",
        source,
        code,
        "resolver.duplicateDeclaration",
        { kind, name },
      ),
    );

  // Processes are keyed by qualified name (owner + name). Anonymous bare-step
  // wrappers (§4.13) carry no name and may legitimately repeat. A decl whose
  // `in <Target>` placement FAILED is emitted unscoped only to stay renderable
  // — it never entered the unscoped scope, so it doesn't compete for names
  // there (the failed placement already carries its own error).
  // v0.11: every key below is prefixed by the declaration's LANDED space
  // (D1a — placed decls carry their target's space), so the same qualified
  // name in sibling spaces stays legal while same-space duplicates keep
  // today's codes. Spaceless prefixes are empty (flat keys, byte-identical).
  const scopePrefix = (space: QualifiedName | undefined): string =>
    `${spacedKey(space, "")}`;

  const procSeen = new Set<string>();
  for (const p of processes) {
    if (p.anonymous || p.name === "" || p.placementFailed) continue;
    const key = scopePrefix(p.space) + [...(p.ownerPath ?? []), p.name].join(".");
    if (procSeen.has(key)) report(key, "process", "DUPLICATE_PROCESS", p.nameSource ?? p.source);
    else procSeen.add(key);
  }

  // Subprocesses are keyed by owner scope + name (the lookup key `do X` uses).
  const subSeen = new Set<string>();
  for (const s of subprocesses) {
    const key = `${scopePrefix(s.space)}${(s.ownerPath ?? []).join(".")}#${s.name}`;
    if (subSeen.has(key))
      report(s.name, "subprocess", "DUPLICATE_SUBPROCESS", s.nameSource ?? s.source);
    else subSeen.add(key);
  }

  // Views are keyed by scope + name (v0.10 §5.2: a view may be module-scoped),
  // so the same short name under two different owners stays legal. Failed
  // placements are excluded — same rationale as processes above.
  const viewSeen = new Set<string>();
  for (const v of views) {
    if (v.placementFailed) continue;
    const key = `${scopePrefix(v.space)}${(v.ownerPath ?? []).join(".")}#${v.name}`;
    if (viewSeen.has(key)) report(v.name, "view", "DUPLICATE_VIEW", v.nameSource ?? v.source);
    else viewSeen.add(key);
  }
}

function validateCascadeDrops(
  modules: readonly ResolvedModule[],
  diagnostics: Diagnostic[],
  ancestorAspects: ReadonlySet<string> = new Set(),
): void {
  for (const m of modules) {
    for (const d of m.body.cascadeDrops ?? []) {
      if (d.kind !== "aspect" || d.matchedLocally) continue;
      const key = d.target.join(".");
      if (!ancestorAspects.has(key)) {
        diagnostics.push(
          makeDiag(
            "error",
            d.source,
            "ASPECT_DROP_NOT_INHERITED",
            "resolver.aspectDropNotInherited",
            { key },
          ),
        );
      }
    }
    const childAspects = new Set(ancestorAspects);
    for (const l of m.body.aspects) {
      if (l.value !== undefined) childAspects.add(l.path.join("."));
    }
    validateCascadeDrops(m.modules, diagnostics, childAspects);
  }
}

/**
 * Steps 4-6 of resolution as a standalone, reusable function: given the
 * already per-decl-resolved modules plus the raw process/subprocess/view
 * declarations, re-parent `in <Parent>` orphans into a tree, then scope
 * processes & subprocesses to their owning module's qualified path.
 *
 * Extracted so the incremental compiler (`incremental.ts`) assembles the
 * exact same model from its keyed store without re-running `resolveModule`
 * on unchanged declarations. Pure: only appends to `diagnostics`.
 */
export interface AssembleParts {
  readonly types: readonly TypeDecl[];
  readonly registry: ResolvedTypeRegistry;
  readonly processes: readonly ProcessDecl[];
  readonly subprocesses: readonly SubprocessDecl[];
  readonly views: readonly ViewDeclV2[];
  /** v0.10 §3 / VIEWS.md §8: top-level `lens` declarations (see
   *  {@link ResolvedModel.lenses}). Optional — hand-built parts omit it. */
  readonly lenses?: readonly LensDecl[];
  /** Process-tests P0/P7: top-level `test` declarations (see
   *  {@link ResolvedModel.tests}). Optional — hand-built parts omit it. */
  readonly tests?: readonly TestDecl[];
  /** Top-level `decoration Name { … }` declarations and the registry built from
   *  them (with the lifecycle built-ins seeded). Optional — hand-built parts
   *  omit both and fall back to a seeded-only registry. */
  readonly decorationDecls?: readonly DecorationDecl[];
  readonly decorationRegistry?: DecorationRegistry;
  /** Wall-clock instant `now` is placed against on the derived timeline.
   *  Injectable so goldens and tests are not hostage to the clock. */
  readonly nowMs?: number;
  readonly spaces?: ReadonlyMap<string, QualifiedName>;
  /** v0.9.0 §1.2: top-level `on <plane> { … }` inclusion blocks (plane presence
   *  for modules declared elsewhere). Fed to `computePresence` so the presence —
   *  and therefore the plane-process instance actors the IR resolves — is
   *  identical on the cold and incremental paths. */
  readonly onInclusions?: readonly OnInclusion[];
}

/** Outputs of the five whole-model assembly passes inside {@link assembleModel}
 *  (root uniqueness, scoped process/subprocess collection, flat-declaration
 *  uniqueness, cascade-drop validation) — all pure functions of tree structure
 *  + the process/subprocess/view declaration sets, so the incremental compiler
 *  (A1) caches them and replays them verbatim on an assembly-neutral edit.
 *  `assemblyDiags` is every diagnostic those passes emit (unknown `in`-parent,
 *  DUPLICATE_MODULE/PROCESS/SUBPROCESS/VIEW, ASPECT_DROP_NOT_INHERITED),
 *  captured beside the arrays so a cache hit replays them instead of
 *  re-walking the tree. */
export interface AssemblyOutputs {
  readonly allProcesses: ProcessDecl[];
  readonly allSubprocesses: SubprocessDecl[];
  /** Top-level + module-scoped views, `ownerPath` stamped (v0.10 §5.2). */
  readonly allViews: ViewDeclV2[];
  readonly assemblyDiags: readonly Diagnostic[];
}

/** In/out cache holder for {@link AssemblyOutputs}: `entry` present → the five
 *  passes are skipped and the entry is replayed; `entry` absent → they run
 *  fresh and fill it. The cold path passes no holder at all. */
export interface AssemblyCacheHolder {
  entry?: AssemblyOutputs;
}

export function assembleModel(
  resolvedModules: readonly ResolvedModule[],
  parts: AssembleParts,
  diagnostics: Diagnostic[],
  /** Optional per-decl IR memo (incremental path only). The cold path omits it
   *  and always builds fresh, so `assembleModel` output is identical either way
   *  (the equivalence invariant) — the cache only avoids rebuilding UNCHANGED
   *  processes' IR after an edit. */
  irCache?: ProcessModelCache,
  /** Optional precomputed presence (incremental path only). The cold path
   *  omits it and always computes fresh, so the equivalence invariant holds by
   *  construction; the incremental caller passes its cached copy ONLY when no
   *  presence input changed since it was computed (`presenceDirty`,
   *  incremental.ts), making the two byte-identical. */
  presence?: PresenceInfo,
  /** Optional cache holder for the five assembly passes (incremental path
   *  only). A pre-filled `entry` skips the passes and replays their arrays +
   *  diagnostics verbatim; an empty holder captures fresh outputs for the next
   *  edit. The cold path omits the holder and always computes, so the
   *  equivalence invariant holds by construction; the incremental caller
   *  pre-fills it ONLY when no assembly input changed (`assemblyDirty`,
   *  incremental.ts), making the two byte-identical. */
  assemblyCache?: AssemblyCacheHolder
): ResolvedModel {
  // 4. Re-parent `in <Parent>`-declared modules into their target's
  //    children. Yields a tree where every flat declaration is reachable
  //    via `modules` recursion alone.
  const tree = applyInClauses(resolvedModules, diagnostics);

  let allProcesses: ProcessDecl[];
  let allSubprocesses: SubprocessDecl[];
  let allViews: ViewDeclV2[];
  if (assemblyCache?.entry) {
    // A1: assembly-neutral edit — replay the cached pass outputs and their
    // emitted diagnostics verbatim (same append position as the compute path).
    ({ allProcesses, allSubprocesses, allViews } = assemblyCache.entry);
    diagnostics.push(...assemblyCache.entry.assemblyDiags);
  } else {
    // Compute path (always taken cold). The passes run into a LOCAL array so
    // their emitted diagnostics can be captured beside the output arrays; the
    // append below keeps the exact order the direct pushes produced.
    const assemblyDiags: Diagnostic[] = [];

    // 4b. Enforce module-name uniqueness at the root scope. A body already
    //     enforces this for its children (validateOneOpPerTarget), but the
    //     root/file scope is not a body, so before this two `service Foo`
    //     declarations in different files silently coexisted as separate
    //     nodes — references resolve to one, the other floats disconnected.
    //     A module name is unique within its scope; extend a module across
    //     files with `in <Parent>`, not by re-declaring it.
    validateRootModuleUniqueness(tree, assemblyDiags);

    // v0.11 (D1a): declaring-file space for FREE-STANDING declarations;
    // module-scoped ones take their owning module's landed space inside the
    // collectors. Shared by the cold and incremental paths (both run this
    // compute path or replay its cached output), so stamping is identical.
    const spaceOfDecl = (d: { source?: SourceSpan }): QualifiedName | undefined =>
      parts.spaces && d.source?.file ? parts.spaces.get(d.source.file) : undefined;

    // 5. Collect module-scoped processes (body-nested + top-level orphans
    //    with `in <ModulePath>`) into the flat `processes` array, stamping
    //    `ownerPath` on each. Diagnoses unknown parents and qualified-name
    //    collisions.
    allProcesses = collectScopedProcesses(tree, parts.processes, assemblyDiags, spaceOfDecl);

    // 6. Collect module-scoped subprocesses (stamped onto every instance
    //    via template inheritance, or declared directly on an instance
    //    body) and merge with top-level free-standing subprocesses.
    allSubprocesses = collectScopedSubprocesses(tree, parts.subprocesses, spaceOfDecl);

    // 6b. Collect module-scoped views (v0.10 §5.2: body-nested + `in`-placed)
    //     into the flat `views` array, stamping `ownerPath` — the process
    //     precedent. Diagnoses dangling `in` targets.
    allViews = collectScopedViews(tree, parts.views, assemblyDiags, spaceOfDecl);

    // Name-uniqueness for the flat declaration lists (processes/views/
    // subprocesses) — the root analogue of the per-body one-op check.
    validateUniqueDeclarations(allProcesses, allSubprocesses, allViews, assemblyDiags);

    // View-instance merge conflicts (a second `story`, a second representation,
    // a missing parent). The projection produces these for the board; running
    // them here is what makes `archlang validate` and the LSP report them too.
    assemblyDiags.push(...validateViewInstances(allViews));

    // Deferred cascade-aware drop validation (§4.9) — runs on the assembled tree
    // so both the full (`resolveFile`) and incremental paths report identically.
    validateCascadeDrops(tree, assemblyDiags);

    diagnostics.push(...assemblyDiags);
    if (assemblyCache) assemblyCache.entry = { allProcesses, allSubprocesses, allViews, assemblyDiags };
  }

  // Always present — a consumer asking "is this name a decoration?" must get the
  // seeded built-ins even when the model declares none of its own.
  const decorationRegistry = parts.decorationRegistry ?? buildDecorationRegistry([]);
  // The timeline is derived here, once, from every marker the assembled model
  // mentions: whatever is written IS the axis. Nothing declares it, so there is
  // no way for a marker to be off-axis and nothing to keep in sync.
  const markers = collectModelMarkers(
    { modules: tree, processes: allProcesses, subprocesses: allSubprocesses, types: parts.types },
    decorationRegistry,
    parts.registry,
  );
  const timeline = markers.length
    ? buildTimeline(markers, { nowMs: parts.nowMs })
    : EMPTY_TIMELINE;

  const base: ResolvedModel = {
    types: parts.types,
    registry: parts.registry,
    modules: tree,
    processes: allProcesses,
    subprocesses: allSubprocesses,
    views: allViews,
    ...(parts.lenses?.length ? { lenses: parts.lenses } : {}),
    ...(parts.tests?.length ? { tests: parts.tests } : {}),
    ...(parts.decorationDecls?.length ? { decorationDecls: parts.decorationDecls } : {}),
    decorationRegistry,
    timeline,
    spaces: parts.spaces,
  };

  // v0.9.0 §1.1: planar presence is computed HERE (before the IR) so the
  // per-process IR can resolve membership instance actors (§1.5) from
  // `instancesAt`. Attached to the model both paths (cold + incremental) share,
  // so presence — and every instance-actor edge/diagnostic derived from it — is
  // byte-identical either way.
  const model: ResolvedModel = {
    ...base,
    onInclusions: parts.onInclusions,
    presence: presence ?? computePresence(base, parts.onInclusions),
  };

  // v0.8: build the resolved per-process IR from the RAW decls (parts.*, tree
  // bodies) — owner resolution must see omitted heads, so it runs off the raw
  // AST, not the implicit-caller-filled `allProcesses`. Carried through
  // `applyCascade` unchanged (it spreads `...model`).
  const ir = buildProcessModels(model, parts.processes, parts.subprocesses, irCache);
  return { ...model, processModels: ir.processModels, subprocessModels: ir.subprocessModels };
}

/**
 * Lift body-scoped subprocesses (those declared on a `module` body, or
 * stamped onto an instance from its `type module …` template) into the
 * model's flat `subprocesses` array. Each entry's `ownerPath` is set to
 * the qualified path of the owning module; lookup from a process's
 * `do X(...)` walks owner-scope upward then falls back to free-standing
 * top-level subprocesses.
 */
function collectScopedSubprocesses(
  roots: readonly ResolvedModule[],
  topLevel: readonly SubprocessDecl[],
  spaceOf?: (d: { source?: SourceSpan }) => QualifiedName | undefined
): SubprocessDecl[] {
  const out: SubprocessDecl[] = [];
  function walk(modules: readonly ResolvedModule[], parentPath: readonly Name[]): void {
    for (const m of modules) {
      const full = [...parentPath, m.name];
      // `scopeSubprocess` sets `ownerPath`; the bare heads of a module-body
      // subprocess inherit the module (or its `on Caller`) when the IR builder
      // resolves them against that owner. Landed space = the owning module's (D1a).
      for (const sp of m.body.subprocesses) {
        out.push(scopeSubprocess(sp, full as QualifiedName, m.space));
      }
      walk(m.modules, full);
    }
  }
  walk(roots, []);
  // Free-standing top-level subprocesses follow (ownerPath stays undefined; the
  // fill uses `on Caller` if present, else leaves a genuinely empty head to flag).
  for (const sp of topLevel) out.push(scopeSubprocess(sp, undefined, spaceOf?.(sp)));
  return out;
}

/**
 * v0.10 §5.2 — flatten module-scoped views (body-nested first, tree order,
 * `ownerPath` = the owning module's qualified path) and place top-level
 * `in <Module> view …` declarations. Targets resolve like every other
 * `in <Target>` (§4.16): exact path, else a unique hoisted suffix (§4.11);
 * ambiguous → AMBIGUOUS_MODULE_REF, dangling → IN_TARGET_NOT_FOUND — either
 * failure is emitted unscoped (and `placementFailed`-flagged) so the rest of
 * the model stays renderable. The exact process-collection posture.
 */
function collectScopedViews(
  roots: readonly ResolvedModule[],
  topLevel: readonly ViewDeclV2[],
  diagnostics: Diagnostic[],
  spaceOf?: (d: { source?: SourceSpan }) => QualifiedName | undefined
): ViewDeclV2[] {
  const out: ViewDeclV2[] = [];
  // Landed space (D1a): the owning module's for body-nested views, the
  // placement target's for `in`-placed ones, the declaring file's for
  // top-level ones. Conditional spread so spaceless models stay byte-stable.
  const spaced = (v: ViewDeclV2, space: QualifiedName | undefined): ViewDeclV2 =>
    space && space.length > 0 ? { ...v, space } : v;
  const index = buildPlacementIndex(roots, (m, full) => {
    for (const v of m.body.views ?? []) out.push(spaced({ ...v, ownerPath: full }, m.space));
  });
  for (const v of topLevel) {
    if (!v.parentPath || v.parentPath.length === 0) {
      out.push(spaced(v, spaceOf?.(v)));
      continue;
    }
    const key = v.parentPath.join(".");
    const hit = resolvePlacement(index, key);
    if (hit.kind !== "ok") {
      diagnostics.push(placementFailureDiag(hit, key, v.parentSource ?? v.source));
      out.push(spaced({ ...v, parentPath: undefined, parentSource: undefined, placementFailed: true }, spaceOf?.(v)));
      continue;
    }
    out.push(spaced({ ...v, ownerPath: hit.segs }, hit.node.space));
  }
  return out;
}

/**
 * v0.10 §4 — collect every policy with its resolved scope. Module-body
 * policies come from the resolved tree (`ownerPath` = the owning module's
 * qualified path); `topLevel` is the file-root declaration set (the caller
 * already walks `ParsedFile.declarations`), whose `in <Module>`-placed entries
 * resolve their target here like every other `in <Target>` (§4.16): exact
 * path, else a unique hoisted suffix (§4.11); ambiguous →
 * AMBIGUOUS_MODULE_REF, dangling → IN_TARGET_NOT_FOUND (either failure is
 * emitted unscoped). A policy's `ownerPath` restricts evaluation to that
 * module's SUBTREE — `evalPolicy` intersects subjects with the scope (edges by
 * source endpoint) and resolves selector names from the module context first.
 */
export function collectScopedPolicies(
  model: ResolvedModel,
  topLevel: readonly PolicyDecl[]
): { policies: PolicyDecl[]; diagnostics: Diagnostic[] } {
  const diagnostics: Diagnostic[] = [];
  const out: PolicyDecl[] = [];
  const index = buildPlacementIndex(model.modules, (m, full) => {
    for (const p of m.body.policies ?? []) out.push({ ...p, ownerPath: full });
  });
  for (const p of topLevel) {
    if (!p.parentPath || p.parentPath.length === 0) {
      out.push(p);
      continue;
    }
    const key = p.parentPath.join(".");
    const hit = resolvePlacement(index, key);
    if (hit.kind !== "ok") {
      diagnostics.push(placementFailureDiag(hit, key, p.parentSource ?? p.source));
      out.push({ ...p, parentPath: undefined, parentSource: undefined });
      continue;
    }
    out.push({ ...p, ownerPath: hit.segs });
  }
  return { policies: out, diagnostics };
}

/**
 * Flatten body-nested processes and apply top-level `in` clauses.
 *
 * Inputs:
 *   - `roots`: the resolved module forest after `applyInClauses` (so every
 *     module reachable via `.modules` is in its final location and we can
 *     compute owner paths).
 *   - `topLevelProcesses`: the flat list of `process` declarations parsed
 *     outside any module body. Each may carry a `parentPath` from an
 *     `in <ModulePath>` clause.
 *
 * Output: a single list of ProcessDecls. Module-scoped entries carry
 * `ownerPath` (qualified path of the owning module). Free-standing entries
 * (no owner) have `ownerPath` undefined. Orphans whose `parentPath` does
 * not resolve produce a diagnostic and remain free-standing (owner unset)
 * so the rest of the model stays renderable.
 */
function collectScopedProcesses(
  roots: readonly ResolvedModule[],
  topLevelProcesses: readonly ProcessDecl[],
  diagnostics: Diagnostic[],
  spaceOf?: (d: { source?: SourceSpan }) => QualifiedName | undefined
): ProcessDecl[] {
  // Note: we deliberately do not flag duplicate qualified names here. The
  // resolver is tolerant of name collisions across the input set (matching
  // existing behavior for modules in concatenated corpora). A dedicated
  // validation pass can elevate duplicates to errors if desired.
  return iterProcessScopes(roots, topLevelProcesses, diagnostics, spaceOf).map((s) =>
    scopeProcess(s.decl, s.ownerPath, s.space)
  );
}

/**
 * A process's decl paired with its resolved `ownerPath`, before `scopeProcess`
 * (no nested-subprocess scoping). Steps stay exactly as WRITTEN — an omitted
 * head is `from: []`, a `|` fan / `\` snake head carries only its flag — and the
 * {@link ProcessModel} IR builder is the single place that resolves them
 * (§4.13.4). Keeping the written form is what lets the IR record the
 * `inherited` / `ditto` / `snake` syntactic form for the backward printer.
 */
export interface ProcessScope {
  readonly decl: ProcessDecl;
  readonly ownerPath?: QualifiedName;
  /** v0.11 (D1a): landed identity space — the owning module's for scoped
   *  processes, the declaring file's for free-standing ones. Optional; the
   *  IR builder ignores it. */
  readonly space?: QualifiedName;
}

/**
 * Enumerate every process with its resolved `ownerPath`, the single owner-path
 * traversal shared by {@link collectScopedProcesses} (which then `scopeProcess`s
 * each) and the IR builder (which builds from the raw decl). Pass `diagnostics`
 * to emit `IN_TARGET_NOT_FOUND` for a dangling `in <Parent>`; omit it (IR build)
 * to resolve owner-paths silently — a dangling parent yields `ownerPath:
 * undefined` either way, so both consumers agree on placement.
 */
/**
 * The parentless twin of an `in <Parent> process` decl (`parentPath` stripped so
 * the scoped process is emitted at top level). MEMOISED by the original decl
 * identity: without this, every `iterProcessScopes` call minted a fresh clone, so
 * the IR cache (a `WeakMap` on the decl) never hit for an in-clause process and
 * rebuilt its IR every snapshot. A stable clone → stable cache key. The
 * `placementFailed` variant (its own memo — the two clones differ) marks a decl
 * whose target didn't resolve, keeping it out of the unscoped uniqueness bucket.
 */
const parentlessClones = new WeakMap<ProcessDecl, ProcessDecl>();
const failedPlacementClones = new WeakMap<ProcessDecl, ProcessDecl>();
function parentlessProc(proc: ProcessDecl, placementFailed = false): ProcessDecl {
  const memo = placementFailed ? failedPlacementClones : parentlessClones;
  let clone = memo.get(proc);
  if (!clone) {
    clone = {
      ...proc,
      parentPath: undefined,
      parentSource: undefined,
      ...(placementFailed ? { placementFailed: true as const } : null),
    };
    memo.set(proc, clone);
  }
  return clone;
}

export function iterProcessScopes(
  roots: readonly ResolvedModule[],
  topLevelProcesses: readonly ProcessDecl[],
  diagnostics?: Diagnostic[],
  /** v0.11 (D1a): declaring-file space lookup for FREE-STANDING processes;
   *  scoped ones take their owning module's landed space. Optional — the IR
   *  builder omits it (it ignores `space`). */
  spaceOf?: (d: { source?: SourceSpan }) => QualifiedName | undefined
): ProcessScope[] {
  const out: ProcessScope[] = [];

  // The shared §4.16 placement index (exact qualified path + hoisted suffix,
  // §4.11) so `in` clauses can target deeply nested modules. Body-nested
  // processes are lifted during the same traversal, children first — the
  // historical `model.processes` array order.
  const index = buildPlacementIndex(
    roots,
    (m, full) => {
      // Lift body-nested processes — ownerPath is the module's qualified
      // path, landed space the module's (D1a).
      for (const proc of m.body.processes) {
        out.push({ decl: proc, ownerPath: full, space: m.space });
      }
    },
    "children-first"
  );

  // Top-level processes, possibly with `in` clauses.
  for (const proc of topLevelProcesses) {
    if (!proc.parentPath || proc.parentPath.length === 0) {
      out.push({ decl: proc, ownerPath: undefined, space: spaceOf?.(proc) });
      continue;
    }
    const key = proc.parentPath.join(".");
    const hit = resolvePlacement(index, key);
    if (hit.kind !== "ok") {
      diagnostics?.push(placementFailureDiag(hit, key, proc.parentSource ?? proc.source));
      // Strip the orphan link so the process is still emitted at top level —
      // `placementFailed`-flagged, so it stays out of the unscoped uniqueness
      // bucket (it never entered the unscoped scope).
      out.push({ decl: parentlessProc(proc, true), ownerPath: undefined, space: spaceOf?.(proc) });
      continue;
    }
    // Placed: identity follows where the decl LANDS — the target's space.
    out.push({ decl: parentlessProc(proc), ownerPath: hit.segs, space: hit.node.space });
  }

  return out;
}

/**
 * Stamp `ownerPath` on a process and recursively scope every
 * subprocess declared inside it so lookup keys disambiguate
 * `Owner.Process.Sub` across modules. The subprocess's owner is the
 * process's fully-qualified name (so two `Helper` subprocesses inside
 * two different processes don't collide).
 */
function scopeProcess(
  proc: ProcessDecl,
  ownerPath: QualifiedName | undefined,
  space?: QualifiedName
): ProcessDecl {
  const procQualified: QualifiedName = ownerPath && ownerPath.length > 0
    ? ([...ownerPath, proc.name] as QualifiedName)
    : ([proc.name] as QualifiedName);
  const subs = proc.subprocesses
    ? proc.subprocesses.map((sp) => scopeSubprocess(sp, procQualified, space))
    : undefined;
  return {
    ...proc,
    ownerPath,
    subprocesses: subs,
    // v0.11 (D1a): landed identity space, stamped like ownerPath.
    ...(space && space.length > 0 ? { space } : {}),
  };
}

function scopeSubprocess(
  sp: SubprocessDecl,
  ownerPath: QualifiedName | undefined,
  space?: QualifiedName
): SubprocessDecl {
  const base: QualifiedName = (ownerPath ?? []) as QualifiedName;
  const childOwner: QualifiedName = [...base, sp.name] as QualifiedName;
  const nested = sp.subprocesses
    ? sp.subprocesses.map((child) => scopeSubprocess(child, childOwner, space))
    : undefined;
  return {
    ...sp,
    ownerPath,
    subprocesses: nested,
    // v0.11 (D1a): landed identity space, stamped like ownerPath.
    ...(space && space.length > 0 ? { space } : {}),
  };
}

/**
 * Move every top-level module that has a `parentPath` into its target's
 * `modules` (resolved children) array. Operates on a depth-first index of
 * the entire forest so a flat-declared module can target a deeply-nested
 * parent. Modules whose parent can't be resolved stay at the top with a
 * diagnostic.
 */
/**
 * The id used to match a module in a diff and to key it in the UI. Explicit
 * `#id` wins; otherwise we fall back to the module's qualified name so content
 * with no `#id`s still diffs and selects (add/remove/modify work — rename
 * tracking needs a real `#id`, since a rename changes the qualified-name id).
 * Pure (no cloning), so the resolved-model identity the cascade cache relies
 * on is untouched.
 */
export function moduleDiffId(
  m: { id?: StableId; name: string; space?: QualifiedName },
  parentPath: readonly Name[] = [],
): StableId {
  const qual = [...parentPath, m.name].join(".");
  // v0.11 (D2): the landed space prefixes the no-`#id` fallback — two
  // spaces' same-named modules diff/select as distinct entities. Spaceless
  // modules keep the byte-identical pre-v0.11 id.
  return m.id ?? (spacedKey(m.space, qual) as StableId);
}

/**
 * Identity-preserving assembly cache for `applyInClauses`. Keyed by the
 * ORIGINAL resolved-module node, it reuses the previously-assembled output
 * object when a node's final children (original + appended orphans) are
 * identity-unchanged across rebuilds. This is what lets the downstream cascade
 * cache keep hitting even in workspaces that use `in <Parent>` re-parenting:
 * an untouched subtree (or an untouched orphan) keeps its object across edits.
 */
interface InClauseEntry {
  readonly children: readonly ResolvedModule[];
  readonly result: ResolvedModule;
}
const inClauseCache = new WeakMap<ResolvedModule, InClauseEntry>();
const EMPTY_APPEND: readonly ResolvedModule[] = [];

export function _resetInClauseCache(): void {
  // The WeakMap can't be cleared; tests/benches that need a cold assembly
  // simply use fresh ResolvedModule objects (a full reparse). Provided for
  // symmetry with the other pass caches.
}

/**
 * v0.8.2 §4.16 — merge each `in Target { … }` extension block into its target
 * module's body (set union), before resolution. Targets are matched against a
 * top-level module by name (single-segment target); a nested/qualified target,
 * or a target that does not resolve to a top-level module, is a hard
 * IN_TARGET_NOT_FOUND (processes/subprocesses/views are not in the module index,
 * so they cannot be reopened). Returns a new module list with merged bodies.
 */
// Each decl's effective qualified path: lexical nesting, plus a TOP-LEVEL decl's
// own `in`-clause parentPath.
function extDeclPath(d: ModuleDecl, parentSegs: readonly string[]): string[] {
  return parentSegs.length === 0 && d.parentPath && d.parentPath.length > 0
    ? [...d.parentPath, d.name]
    : [...parentSegs, d.name];
}

/** Index a module forest by full path + unique proper suffix, and by body. */
function indexExtTree(modules: readonly ModuleDecl[]): {
  byPath: Set<string>;
  sufCount: Map<string, number>;
  sufOne: Map<string, string>;
  bodyOf: Map<string, Body>;
} {
  const byPath = new Set<string>();
  const sufCount = new Map<string, number>();
  const sufOne = new Map<string, string>();
  const bodyOf = new Map<string, Body>();
  const visit = (d: ModuleDecl, parentSegs: readonly string[]): void => {
    const segs = extDeclPath(d, parentSegs);
    const full = segs.join(".");
    byPath.add(full);
    bodyOf.set(full, d.body);
    for (let k = 1; k < segs.length; k++) {
      const suf = segs.slice(segs.length - k).join(".");
      sufCount.set(suf, (sufCount.get(suf) ?? 0) + 1);
      sufOne.set(suf, full);
    }
    for (const child of d.body.modules) visit(child, segs);
  };
  for (const d of modules) visit(d, []);
  return { byPath, sufCount, sufOne, bodyOf };
}

export function applyExtensionBlocks(
  modules: readonly ModuleDecl[],
  extensions: readonly ExtensionDecl[],
  diagnostics: Diagnostic[]
): ModuleDecl[] {
  if (extensions.length === 0) return [...modules];

  // Fixpoint: an extension may target a module contributed by ANOTHER extension,
  // so resolve+merge what we can each round, re-index the grown tree, and repeat
  // until a round makes no progress. Merge = set union (§4.16); a target resolves
  // by exact path else a UNIQUE suffix (§4.11); truly-unresolvable → error.
  let current: ModuleDecl[] = [...modules];
  let pending = [...extensions];

  // Merge ONE extension per round, re-indexing between each. This makes every
  // extension see the modules that earlier extensions added — so a sibling's new
  // child can make a later `in X` target ambiguous (§4.16). Extensions merge in
  // source order; the merge result itself is order-independent (set union).
  for (;;) {
    const { byPath, sufCount, sufOne, bodyOf } = indexExtTree(current);
    let merged: { full: string; body: Body } | undefined;
    const still: ExtensionDecl[] = [];
    for (const ext of pending) {
      const key = ext.target.join(".");
      const full = byPath.has(key) ? key : sufCount.get(key) === 1 ? sufOne.get(key) : undefined;
      if (full && !merged) {
        merged = { full, body: mergeExtensionBody(bodyOf.get(full) ?? EMPTY_BODY, ext.body) };
      } else {
        still.push(ext);
      }
    }

    if (!merged) {
      // No progress this round → the rest reference an unknown/ambiguous target.
      for (const ext of still) {
        const key = ext.target.join(".");
        const ambiguous = (sufCount.get(key) ?? 0) > 1;
        diagnostics.push(
          makeDiag(
            "error",
            ext.targetSource ?? ext.source,
            "IN_TARGET_NOT_FOUND",
            "resolver.inTargetUnknownTarget",
            { key, ambiguous }
          )
        );
      }
      return current;
    }

    // Path-copy: the single target's body becomes its merged body; recurse.
    const rebuild = (d: ModuleDecl, parentSegs: readonly string[]): ModuleDecl => {
      const segs = extDeclPath(d, parentSegs);
      const body = segs.join(".") === merged!.full ? merged!.body : d.body;
      const newChildren = body.modules.map((c) => rebuild(c, segs));
      const childrenChanged = newChildren.some((c, i) => c !== body.modules[i]);
      if (body === d.body && !childrenChanged) return d;
      return { ...d, body: { ...body, modules: newChildren } };
    };
    current = current.map((d) => rebuild(d, []));

    pending = still;
    if (pending.length === 0) return current;
  }
}

/** Set-union of two module/surface bodies (§4.16). */
function mergeExtensionBody(a: Body, b: Body): Body {
  const cascadeDrops =
    a.cascadeDrops || b.cascadeDrops
      ? [...(a.cascadeDrops ?? []), ...(b.cascadeDrops ?? [])]
      : undefined;
  const optUnion = <T>(x: readonly T[] | undefined, y: readonly T[] | undefined): readonly T[] | undefined =>
    x || y ? [...(x ?? []), ...(y ?? [])] : undefined;
  const views = optUnion(a.views, b.views);
  const policies = optUnion(a.policies, b.policies);
  const queries = optUnion(a.queries, b.queries);
  const tests = optUnion(a.tests, b.tests);
  const lenses = optUnion(a.lenses, b.lenses);
  // §7 presence forms. This merge rebuilds `Body` field by field, so anything
  // not listed is DROPPED — reopening a module with `in X { … }` silently erased
  // its planes: the body `on p1, p2` (also-on), its `on p { … }` blocks (partial
  // presence) and its rosters (which pull a place onto a plane and bind instance
  // actors) all vanished, with no diagnostic.
  const rosters = optUnion(a.rosters, b.rosters);
  const alsoOn = optUnion(a.alsoOn, b.alsoOn);
  const partialOn = optUnion(a.partialOn, b.partialOn);
  return {
    fields: [...a.fields, ...b.fields],
    aspects: [...a.aspects, ...b.aspects],
    descriptions: [...a.descriptions, ...b.descriptions],
    drops: [...a.drops, ...b.drops],
    surfaces: [...a.surfaces, ...b.surfaces],
    interfaces: [...a.interfaces, ...b.interfaces],
    modules: [...a.modules, ...b.modules],
    processes: [...a.processes, ...b.processes],
    subprocesses: [...a.subprocesses, ...b.subprocesses],
    ...(views ? { views } : {}),
    ...(policies ? { policies } : {}),
    ...(queries ? { queries } : {}),
    ...(tests ? { tests } : {}),
    ...(lenses ? { lenses } : {}),
    ...(cascadeDrops ? { cascadeDrops } : {}),
    ...(rosters ? { rosters } : {}),
    ...(alsoOn ? { alsoOn } : {}),
    ...(partialOn ? { partialOn } : {}),
  };
}

// =============================================================================
// §4.16 placement-target resolution — the ONE `in <Target>` helper, shared by
// every placement site: module re-parenting, views, policies, processes.
// =============================================================================

/** A placement-target candidate: the module node + the qualified path it was
 *  indexed under. One node may be indexed under SEVERAL paths (a placed orphan
 *  keeps its pre-placement entries when its subtree is re-indexed under the
 *  resolved path); {@link resolvePlacement} collapses candidates by NODE
 *  identity, so a doubly-indexed node is one candidate — never a false
 *  ambiguity, in either declaration order. */
interface PlacementCand {
  readonly node: ResolvedModule;
  readonly segs: QualifiedName;
  readonly full: string;
}

/** Exact-path + proper-suffix index over a module forest — how an
 *  `in <Target>` clause resolves its target per §4.16 ("targets resolve by the
 *  normal rules", which include §4.11 hoisting). `add` may be called again
 *  mid-pass to index a placed orphan's subtree under its resolved path. */
interface PlacementIndex {
  readonly byPath: Map<string, PlacementCand>;
  readonly bySuffix: Map<string, PlacementCand[]>;
  add(node: ResolvedModule, parentSegs: readonly Name[]): void;
}

function buildPlacementIndex(
  roots: readonly ResolvedModule[],
  /** Fires once per indexed module with its qualified path, so callers can
   *  lift body-scoped declarations during the single traversal. */
  visit?: (m: ResolvedModule, full: QualifiedName) => void,
  /** `visit` order: parents before their children (default), or children
   *  first — process collection lifts nested modules' processes before the
   *  enclosing module's (the historical `model.processes` array order). */
  visitOrder: "parent-first" | "children-first" = "parent-first"
): PlacementIndex {
  const byPath = new Map<string, PlacementCand>();
  const bySuffix = new Map<string, PlacementCand[]>();
  const add = (node: ResolvedModule, parentSegs: readonly Name[]): void => {
    const segs = [...parentSegs, node.name] as QualifiedName;
    const full = segs.join(".");
    const cand: PlacementCand = { node, segs, full };
    byPath.set(full, cand);
    // v0.11 (D3): the space-extended written form (`Alpha.Host`) names the
    // target exactly across any boundary. A declared exact structural path
    // already holding the key wins (exact-first — a module named like a
    // space is never shadowed).
    if (node.space && node.space.length > 0) {
      const extended = [...node.space, ...segs].join(".");
      if (!byPath.has(extended)) byPath.set(extended, cand);
    }
    // Proper suffixes only (shorter than the full path — the full path is the
    // byPath key). A top-level single-segment node contributes none.
    for (let k = 1; k < segs.length; k++) {
      const suf = segs.slice(segs.length - k).join(".");
      const list = bySuffix.get(suf);
      if (list) list.push(cand);
      else bySuffix.set(suf, [cand]);
    }
    if (visitOrder === "parent-first") visit?.(node, segs);
    for (const c of node.modules) add(c, segs);
    if (visitOrder === "children-first") visit?.(node, segs);
  };
  for (const r of roots) add(r, []);
  return { byPath, bySuffix, add };
}

type PlacementHit =
  | { readonly kind: "ok"; readonly node: ResolvedModule; readonly segs: QualifiedName; readonly full: string }
  | { readonly kind: "ambiguous"; readonly candidates: readonly string[] }
  | { readonly kind: "missing" };

/** Resolve an `in <Target>` written path: exact qualified path first, then a
 *  unique §4.11 suffix. Suffix candidates are DISTINCT target modules by node
 *  identity — a node indexed under both its pre- and post-placement paths is
 *  ONE candidate (its most recently indexed path wins), so chained placements
 *  resolve identically in either declaration order. */
function resolvePlacement(
  index: PlacementIndex,
  key: string,
  exclude?: (c: PlacementCand) => boolean
): PlacementHit {
  const exact = index.byPath.get(key);
  if (exact) return { kind: "ok", node: exact.node, segs: exact.segs, full: exact.full };
  const byNode = new Map<ResolvedModule, PlacementCand>();
  for (const c of index.bySuffix.get(key) ?? []) {
    if (exclude?.(c)) continue;
    byNode.set(c.node, c); // last write wins → the node's current path
  }
  if (byNode.size === 1) {
    const c = byNode.values().next().value!;
    return { kind: "ok", node: c.node, segs: c.segs, full: c.full };
  }
  if (byNode.size > 1) {
    return { kind: "ambiguous", candidates: [...byNode.values()].map((c) => c.full).sort() };
  }
  return { kind: "missing" };
}

/** The shared diagnostic for a failed placement — the same two shapes at every
 *  `in <Target>` site: ambiguous → AMBIGUOUS_MODULE_REF listing the candidate
 *  full paths (the process/edge hoisting-ambiguity precedent, validation.ts);
 *  zero candidates → IN_TARGET_NOT_FOUND. */
function placementFailureDiag(
  hit: Exclude<PlacementHit, { kind: "ok" }>,
  key: string,
  src: SourceSpan | undefined
): Diagnostic {
  return hit.kind === "ambiguous"
    ? makeDiag(
        "error",
        src,
        "AMBIGUOUS_MODULE_REF",
        "resolver.inTargetAmbiguous",
        { key, candidates: hit.candidates.join(", ") }
      )
    : makeDiag("error", src, "IN_TARGET_NOT_FOUND", "resolver.inTargetUnknownParent", { key });
}

function applyInClauses(
  roots: readonly ResolvedModule[],
  diagnostics: Diagnostic[]
): ResolvedModule[] {
  // Fast path: no `in <Parent>` orphans → no re-parenting, so return the input
  // verbatim. Preserves ResolvedModule identity for the common case.
  if (!roots.some((m) => m.parentPath && m.parentPath.length > 0)) return [...roots];

  // Index ALL roots (orphans included) by qualified path + proper suffix,
  // using ORIGINAL references (no cloning — we path-copy only the spine at the
  // end). The shared §4.16 placement index: an `in <Target>` clause may name
  // its parent by a hoisted path-suffix (§4.11), exactly as a process/edge
  // reference may — `in B` resolves to `P.B` when `B` is a unique leaf.
  const index = buildPlacementIndex(roots);

  // Partition.
  const realRoots: ResolvedModule[] = [];
  const orphans: ResolvedModule[] = [];
  for (const m of roots) {
    if (m.parentPath && m.parentPath.length > 0) orphans.push(m);
    else realRoots.push(m);
  }
  if (orphans.length === 0) return [...roots];

  // Resolve each orphan to a target (by reference). Records appended children
  // per original target node; re-indexes the orphan subtree under its new path
  // so chains (`X in Platform.AuthService`) resolve after their parent settles.
  const appended = new Map<ResolvedModule, ResolvedModule[]>();
  const unresolved: ResolvedModule[] = [];
  for (const orphan of orphans) {
    const targetKey = orphan.parentPath!.join(".");
    const src = orphan.decl.parentSource ?? orphan.decl.source;
    // Suffix arm only: the orphan is indexed at its top-level name
    // pre-placement, so its OWN descendants sit under `<name>.…`; exclude
    // them — a decl can't be placed inside its own subtree, and letting it
    // count would falsely read as ambiguous (`in B` next to the orphan's own
    // nested `B`).
    const selfPrefix = `${orphan.name}.`;
    const hit = resolvePlacement(
      index,
      targetKey,
      (c) => c.full === orphan.name || c.full.startsWith(selfPrefix)
    );
    if (hit.kind !== "ok") {
      diagnostics.push(placementFailureDiag(hit, targetKey, src));
      unresolved.push(orphan);
      continue;
    }
    let list = appended.get(hit.node);
    if (!list) appended.set(hit.node, (list = []));
    list.push(orphan);
    // Re-index the placed subtree under its resolved path so chained
    // placements (`in X`, then `in X`-child) resolve after their parent
    // settles. The stale pre-placement entries stay in the index, but
    // `resolvePlacement` collapses them with the fresh ones by node identity.
    index.add(orphan, hit.segs);
  }

  // Path-copy: clone ONLY nodes whose children changed (gained an orphan, or a
  // descendant did); return untouched nodes by reference. `clearParent` strips
  // the `parentPath` marker off a re-parented orphan. `landedSpace` (v0.11
  // D1a) is the identity space of the subtree's landing root: an orphan
  // placed into another space's subtree is re-stamped — with its whole
  // subtree — to the target's space, so identity follows where a declaration
  // LANDS, not where its text lives.
  function materialize(
    node: ResolvedModule,
    clearParent: boolean,
    landedSpace: QualifiedName | undefined,
  ): ResolvedModule {
    const spaceKeyOf = (s: QualifiedName | undefined): string => (s ? s.join(".") : "");
    const restamp = spaceKeyOf(node.space) !== spaceKeyOf(landedSpace);
    const extra = appended.get(node) ?? EMPTY_APPEND;
    const newChildren = node.modules.map((c) => materialize(c, false, landedSpace));
    const extraChildren = extra.map((o) => materialize(o, true, landedSpace));
    const sameChildren =
      extra.length === 0 && newChildren.every((c, i) => c === node.modules[i]);
    if (!clearParent && !restamp && sameChildren) return node; // identity preserved

    const finalChildren = extra.length ? [...newChildren, ...extraChildren] : newChildren;
    // Reuse a previously-assembled result when the final children match, so the
    // assembled object is identity-stable across rebuilds (cascade keeps hitting).
    const cached = inClauseCache.get(node);
    if (
      cached &&
      (clearParent ? cached.result.parentPath === undefined : cached.result.parentPath === node.parentPath) &&
      spaceKeyOf(cached.result.space) === spaceKeyOf(landedSpace) &&
      cached.children.length === finalChildren.length &&
      cached.children.every((c, i) => c === finalChildren[i])
    ) {
      return cached.result;
    }
    const result: ResolvedModule = {
      ...node,
      ...(clearParent ? { parentPath: undefined } : null),
      ...(restamp
        ? landedSpace && landedSpace.length > 0
          ? { space: landedSpace }
          : { space: undefined }
        : null),
      modules: finalChildren,
    };
    inClauseCache.set(node, { children: finalChildren, result });
    return result;
  }

  return [
    ...realRoots.map((r) => materialize(r, false, r.space)),
    // Unresolved placements emit unscoped in their OWN declaring space.
    ...unresolved.map((o) => materialize(o, true, o.space)),
  ];
}

// =============================================================================
// Kind registry
// =============================================================================

/**
 * True if `childType` is the same as or a registered subtype of `parentType`.
 * Walks the parent chain in the registry; while the registry is still being
 * filled (template stamping), falls back to the chain oracle so the answer
 * never depends on declaration order.
 */
function isSubtypeOf(
  childType: string,
  parentType: string,
  registry: ResolvedTypeRegistry
): boolean {
  if (childType === parentType) return true;
  // chain is parent-chain (excluding self). Walk it.
  const chain = registry.kinds.get(childType)?.chain ?? registry.chainOracle?.get(childType);
  if (!chain) return false;
  for (const ancestor of chain) {
    if (ancestor === parentType) return true;
  }
  return false;
}

export function buildRegistry(
  types: readonly TypeDecl[],
  diagnostics: Diagnostic[],
  spaceOf?: (t: TypeDecl) => QualifiedName | undefined
): ResolvedTypeRegistry {
  const spaceFor = (t: TypeDecl): QualifiedName | undefined => spaceOf?.(t);

  // Index types by qualified id (`<space>::<name>`). With no spaces every id
  // is `::<name>`, so this is the old name-keyed behavior exactly. Two types
  // collide only when they share a space AND a name.
  const byIdDecls = new Map<string, TypeDecl>();
  for (const t of types) {
    const id = kindId(spaceFor(t), t.name);
    if (byIdDecls.has(id)) {
      diagnostics.push(
        makeDiag("error", t.nameSource ?? t.source, "DUPLICATE_TYPE", "resolver.duplicateType", { name: t.name })
      );
      continue;
    }
    byIdDecls.set(id, t);
  }

  const byId = new Map<string, ResolvedType>();
  const kinds = new Map<string, ResolvedType>();

  // Record a resolved kind under its id and in the name-keyed view. A root
  // declaration (id starts with `::`) wins the name slot over a spaced one.
  const record = (id: string, name: string, rt: ResolvedType): void => {
    byId.set(id, rt);
    if (!kinds.has(name) || id.startsWith("::")) kinds.set(name, rt);
  };

  // Pre-seed base kinds at the package root. Only `module` carries non-empty
  // defaults — every module instance gets the builtin `arch-module` widget
  // config stamped through the same template-merge path as user kinds.
  for (const baseName of BASE_KINDS) {
    record(kindId(undefined, baseName), baseName, {
      name: baseName,
      baseType: baseName as BaseType,
      chain: [],
      stampedBody: baseName === "module" ? BUILTIN_MODULE_BODY : EMPTY_BODY,
    });
  }

  // Pre-seed stdlib interface kinds (root) — they default to base kind
  // 'interface' when no `type interface foo` is declared. An explicit
  // declaration takes precedence in the pass below.
  for (const name of STDLIB_INTERFACE_KINDS) {
    const rootId = kindId(undefined, name);
    if (byId.has(rootId)) continue;
    if (byIdDecls.has(rootId)) continue; // declared explicitly at root
    record(rootId, name, {
      name,
      baseType: "interface",
      chain: [],
      stampedBody: EMPTY_BODY,
    });
  }

  // --- Chain prepass (order-independent subtype oracle) ---------------------
  // The stamping loop below resolves a type as soon as its PARENT is
  // resolved, but the refinement checks inside its body (`required
  // relational S` against an inherited `required database S`) may name kinds
  // that resolve later in the pass — imported synthetics are appended after
  // local declarations, and a local family may be declared below its
  // consumer. Chains depend only on parent links, never on stamped bodies,
  // so compute every declaration's ancestor chain up front; `isSubtypeOf`
  // falls back to this oracle for kinds the loop hasn't recorded yet.
  const chainById = new Map<string, { name: string; chain: readonly string[] }>();
  for (const baseName of BASE_KINDS) {
    chainById.set(kindId(undefined, baseName), { name: baseName, chain: [] });
  }
  for (const name of STDLIB_INTERFACE_KINDS) {
    const rootId = kindId(undefined, name);
    if (!chainById.has(rootId) && !byIdDecls.has(rootId)) {
      chainById.set(rootId, { name, chain: [] });
    }
  }
  {
    const pending = new Set(byIdDecls.keys());
    let advanced = true;
    while (pending.size > 0 && advanced) {
      advanced = false;
      for (const id of [...pending]) {
        const decl = byIdDecls.get(id)!;
        const parentName = decl.parentType;
        const space = spaceFor(decl);
        // Mirror `resolveParentType`'s lookup over the chain entries.
        let parent: { name: string; chain: readonly string[] } | undefined;
        if (!parentName.includes(".")) {
          for (const s of spaceChain(space)) {
            parent = chainById.get(kindId(s, parentName));
            if (parent) break;
          }
        } else {
          const parts = parentName.split(".");
          const typeName = parts[parts.length - 1]!;
          const spacePath = parts.slice(0, -1);
          parent =
            chainById.get(kindId(spacePath, typeName)) ??
            (space ? chainById.get(kindId([...space, ...spacePath], typeName)) : undefined);
        }
        if (!parent) continue;
        chainById.set(id, {
          name: decl.name,
          chain: [...parent.chain, parentName.includes(".") ? parent.name : parentName],
        });
        pending.delete(id);
        advanced = true;
      }
    }
  }
  // Name-keyed view; a root declaration wins the name slot (same rule as
  // `record` below).
  const chainOracle = new Map<string, readonly string[]>();
  for (const [id, entry] of chainById) {
    if (!chainOracle.has(entry.name) || id.startsWith("::")) {
      chainOracle.set(entry.name, entry.chain);
    }
  }
  const stampingRegistry: ResolvedTypeRegistry = { kinds, byId, chainOracle };

  // Resolve each declared type. Iterative passes handle dependency ordering;
  // a parent is looked up nearest-first from the declaration's own space.
  const remaining = new Set(byIdDecls.keys());
  let progress = true;
  while (remaining.size > 0 && progress) {
    progress = false;
    for (const id of [...remaining]) {
      const decl = byIdDecls.get(id)!;
      const space = spaceFor(decl);
      const parentName = decl.parentType;

      const parent = resolveParentType(byId, parentName, space);
      if (!parent) {
        // Parent not yet resolved (or in another space); defer.
        continue;
      }

      const baseType = parent.baseType;

      if (baseType === "process" || baseType === "view") {
        diagnostics.push(
          makeDiag(
            "error",
            decl.parentTypeSource ?? decl.source,
            "TYPE_PROCESS_VIEW_FORBIDDEN",
            "resolver.typeProcessViewForbidden",
            { parentName }
          )
        );
        remaining.delete(id);
        progress = true;
        continue;
      }

      // Stamp: merge parent's stamped body with this type's own body.
      // Pass the partial registry (with the complete chain oracle) so
      // subtype refinements are recognized regardless of resolution order.
      const stampedRaw = mergeBodies(
        parent.stampedBody,
        decl.body,
        diagnostics,
        decl,
        stampingRegistry
      );
      // Every valued field in a type's stamped body is a *template default*
      // from an instance's perspective. Mark provenance so `applyCascade` lets
      // a structural-ancestor cascade value win over it (§4.9 nested-wins).
      const stamped: Body = {
        ...stampedRaw,
        fields: stampedRaw.fields.map((f) =>
          f.value !== undefined && !f.fromTemplate ? { ...f, fromTemplate: true } : f
        ),
      };

      record(id, decl.name, {
        name: decl.name,
        baseType,
        // chain holds bare type names (isSubtypeOf walks it by name); for a
        // qualified parent ref, record the resolved bare name, not the path.
        chain: [...parent.chain, parentName.includes(".") ? parent.name : parentName],
        stampedBody: stamped,
        decl,
        source: decl.source,
        space,
      });

      remaining.delete(id);
      progress = true;
    }
  }

  // Anything left is part of a cycle or has an unknown parent.
  for (const id of remaining) {
    const decl = byIdDecls.get(id)!;
    diagnostics.push(
      makeDiag(
        "error",
        decl.parentTypeSource ?? decl.source,
        "UNKNOWN_PARENT_TYPE",
        "resolver.unknownParentType",
        { parentType: decl.parentType, name: decl.name }
      )
    );
  }

  return { kinds, byId };
}

// =============================================================================
// Body merging (template stamping + instance refinement)
// =============================================================================

/**
 * Merge `incoming` onto `base`, applying refinement / override / drop
 * semantics. Used for both:
 *   - parent type's stamped body + child type's body (template chain)
 *   - kind template + instance body (auto-propagation onto an instance)
 *
 * Semantics:
 *   - Fields with same path: incoming wins (refinement). Diagnostics if the
 *     base had cascade/append modifiers and incoming changes them.
 *   - Aspects with same path: incoming wins, OR if `drop`, removes from base.
 *   - Drops (top-level): remove matching named entity from sub-decls or
 *     fields/aspects.
 *   - Sub-declarations (surfaces, interfaces, modules) by name:
 *       - If `override`: replace base entry.
 *       - If `required` was on base and incoming has content: fulfill.
 *       - Otherwise: refine — recursively merge bodies.
 *   - One-op-per-target rule is enforced within `incoming` (not across
 *     base/incoming).
 */
function mergeBodies(
  base: Body,
  incoming: Body,
  diagnostics: Diagnostic[],
  ctx: { source?: SourceSpan },
  registry?: ResolvedTypeRegistry
): Body {
  // Validate one-op-per-target within `incoming` first.
  validateOneOpPerTarget(incoming, diagnostics);

  // Start with base, apply drops, then layer incoming.
  let fields = [...base.fields];
  let aspects = [...base.aspects];
  // Descriptions do NOT cascade: a description stays on the entity it was
  // declared on and never stamps onto instances/children. Carry only the
  // incoming (own) descriptions; ignore the base (template) ones.
  const descriptions = [...incoming.descriptions];
  let surfaces = [...base.surfaces];
  let interfaces = [...base.interfaces];
  let modules = [...base.modules];
  // Processes carried on a body are not template-merged: a `type module`
  // declaring an inline process would stamp the same process onto every
  // instance, which is rarely useful and would collide on qualified name.
  // For now we pass through the incoming process list and ignore the base
  // (template) processes. Revisit if a "default workflow" pattern emerges.
  const processes = [...incoming.processes];
  // v0.10 §5.2 module-scoped declarations follow the process posture: pass
  // through the incoming (own) declarations, never template-stamp them —
  // stamping a view/policy onto every instance would collide on scoped name.
  const views = incoming.views;
  const policies = incoming.policies;
  const queries = incoming.queries;
  const tests = incoming.tests;
  const lenses = incoming.lenses;
  // Subprocesses ARE template-merged: a `type module` may declare reusable
  // helpers that every instance inherits and any process owned by that
  // instance (or a descendant) can invoke via `do X(...)`. Same merge
  // rule as surfaces/interfaces — incoming overrides on name match.
  const subprocesses = mergeSubprocesses(base.subprocesses, incoming.subprocesses);

  // Tombstones for the structural cascade (§4.9). Carry forward any the base
  // (template chain) recorded, then add the drops applied in this merge.
  const cascadeDrops: CascadeDrop[] = [...(base.cascadeDrops ?? [])];

  // Apply incoming drops.
  for (const drop of incoming.drops) {
    const target = drop.target;
    const fieldsBefore = fields.length;
    const aspectsBefore = aspects.length;
    const beforeCount = surfaces.length + interfaces.length + modules.length + fields.length + aspects.length;

    if (target.length === 1) {
      const head = target[0]!;
      surfaces = surfaces.filter((f) => f.name !== head);
      interfaces = interfaces.filter((i) => i.name !== head);
      modules = modules.filter((m) => m.name !== head);
      // Cascade-group: if the target is the root of an inherited
      // cascade group, drop every field under that path too — the
      // group collapses without its root.
      const groupRoot = fields.find(
        (f) => f.path.length === 1 && f.path[0] === head && f.group,
      );
      if (groupRoot) {
        fields = fields.filter((f) => !pathStartsWith(f.path, target));
      } else {
        fields = fields.filter((f) => f.path[0] !== head || f.path.length !== 1);
      }
      aspects = aspects.filter((l) => l.path[0] !== head || l.path.length !== 1);
    } else {
      // Dotted drop: drop only matching path
      fields = fields.filter((f) => !pathsEqual(f.path, target));
      aspects = aspects.filter((l) => !pathsEqual(l.path, target));
      // For sub-declarations, dotted drop targets a child (like
      // `drop metrics.Emit` removes `Emit` inside `metrics`). Apply by
      // descending and rewriting the matched child's body.
      const head = target[0]!;
      const rest = target.slice(1);
      surfaces = surfaces.map((f) =>
        f.name === head ? { ...f, body: applyDropPath(f.body, rest, ctx, diagnostics) } : f
      );
      modules = modules.map((m) =>
        m.name === head ? { ...m, body: applyDropPath(m.body, rest, ctx, diagnostics) } : m
      );
    }

    // A dropped field/aspect must also break the STRUCTURAL cascade (§4.9 "drop
    // is absolute") — record a tombstone so `applyCascade` stops re-inheriting
    // the path from an ancestor for this module and its descendants.
    if (fields.length < fieldsBefore) {
      cascadeDrops.push({ target, kind: "field", matchedLocally: true, source: drop.source });
    }
    if (aspects.length < aspectsBefore) {
      cascadeDrops.push({ target, kind: "aspect", matchedLocally: true, source: drop.source });
    }

    const afterCount = surfaces.length + interfaces.length + modules.length + fields.length + aspects.length;
    if (beforeCount === afterCount) {
      diagnostics.push(
        makeDiag(
          "error",
          drop.source,
          "DROP_NOT_INHERITED",
          "resolver.dropNotInherited",
          { target: target.join(".") }
        )
      );
    }
  }

  // A `aspects { drop X }` entry is also a cascade tombstone. Whether it matches
  // here (template) or not, the aspect may arrive via structural cascade, so the
  // "not inherited" check is deferred to a cascade-aware pass (§4.9); record
  // `matchedLocally` so that pass only re-checks genuinely-unmatched drops.
  for (const entry of incoming.aspects) {
    if (!entry.drop) continue;
    const matchedLocally = aspects.some((b) => pathsEqual(b.path, entry.path));
    cascadeDrops.push({ target: entry.path, kind: "aspect", matchedLocally, source: entry.source });
  }

  // Layer in incoming additions/refinements.
  fields = mergeFields(fields, incoming.fields, diagnostics);
  aspects = mergeAspects(aspects, incoming.aspects, diagnostics);
  surfaces = mergeSurfaces(surfaces, incoming.surfaces, diagnostics, ctx, registry);
  interfaces = mergeInterfaces(interfaces, incoming.interfaces, diagnostics, ctx, registry);
  modules = mergeModules(modules, incoming.modules, diagnostics, ctx, registry);

  return {
    fields, aspects, descriptions, drops: [], surfaces, interfaces, modules, processes, subprocesses,
    ...(views?.length ? { views } : {}),
    ...(policies?.length ? { policies } : {}),
    ...(queries?.length ? { queries } : {}),
    ...(tests?.length ? { tests } : {}),
    ...(lenses?.length ? { lenses } : {}),
    ...(cascadeDrops.length > 0 ? { cascadeDrops } : {}),
  };
}

function mergeSubprocesses(
  base: readonly SubprocessDecl[],
  incoming: readonly SubprocessDecl[]
): SubprocessDecl[] {
  if (base.length === 0) return [...incoming];
  if (incoming.length === 0) return [...base];
  const result = [...base];
  for (const sp of incoming) {
    const idx = result.findIndex((b) => b.name === sp.name);
    if (idx >= 0) result[idx] = sp;
    else result.push(sp);
  }
  return result;
}

function applyDropPath(
  body: Body,
  path: readonly Name[],
  ctx: { source?: SourceSpan },
  diagnostics: Diagnostic[]
): Body {
  const synthetic: Body = {
    fields: [],
    aspects: [],
    descriptions: [],
    drops: [
      {
        target: path,
        source: ctx.source,
      },
    ],
    surfaces: [],
    interfaces: [],
    modules: [],
    processes: [],
    subprocesses: [],
  };
  return mergeBodies(body, synthetic, diagnostics, ctx);
}

function mergeFields(
  base: readonly FieldDecl[],
  incoming: readonly FieldDecl[],
  diagnostics: Diagnostic[]
): FieldDecl[] {
  // Cascade-group drop pre-pass: when `incoming` redefines a base
  // group root with a different value, drop every base sub-field
  // whose path is under that root. The root itself stays — it will
  // be normally overridden by the merge below.
  let result = [...base];
  for (const f of incoming) {
    if (f.value === undefined) continue;
    const rootIdx = result.findIndex((b) => pathsEqual(b.path, f.path) && b.group);
    if (rootIdx < 0) continue;
    const baseRoot = result[rootIdx]!;
    if (baseRoot.value !== undefined && valuesEqual(baseRoot.value, f.value)) continue;
    // Different value at the group root → drop inherited sub-fields.
    result = result.filter(
      (b) => pathsEqual(b.path, f.path) || !pathStartsWith(b.path, f.path),
    );
  }

  for (const f of incoming) {
    const idx = result.findIndex((b) => pathsEqual(b.path, f.path));
    if (idx >= 0) {
      // Refinement: incoming may set a value where base had `required`,
      // or override an existing value. Merge cascade modifiers from base.
      const baseEntry = result[idx]!;
      const merged: FieldDecl = {
        ...baseEntry,
        ...f,
        // Carry forward cascade/append from base (mode is fixed at type
        // that introduces the field; cannot be redefined downstream).
        cascade: baseEntry.cascade ?? f.cascade,
        append: baseEntry.append ?? f.append,
        // Preserve group-root marker — if base declared this field as
        // a cascade-group root, downstream merges keep it as one even
        // when incoming doesn't re-mark.
        group: baseEntry.group ?? f.group,
        // If incoming provides a value, the field is fulfilled — drop required.
        required: f.value !== undefined ? undefined : f.required ?? baseEntry.required,
        // An incoming value is author-set, not a template default — clear the
        // template provenance so it wins over ancestor cascades (§4.9). Only
        // when incoming has no value does the base's provenance carry through.
        fromTemplate: f.value !== undefined ? f.fromTemplate : baseEntry.fromTemplate,
      };
      // Diagnostic if incoming tries to redefine cascade/append
      if (
        (f.cascade && baseEntry.append) ||
        (f.append && baseEntry.cascade)
      ) {
        diagnostics.push(
          makeDiag(
            "error",
            f.source,
            "CASCADE_MODE_REDEFINED",
            "resolver.cascadeModeRedefined",
            { field: f.path.join(".") }
          )
        );
      }
      result[idx] = merged;
    } else {
      result.push(f);
    }
  }
  return result;
}

function mergeAspects(
  base: readonly AspectEntry[],
  incoming: readonly AspectEntry[],
  diagnostics: Diagnostic[]
): AspectEntry[] {
  let result = [...base];
  for (const entry of incoming) {
    if (entry.drop) {
      // Remove the matched aspect here. The "no matching inherited aspect" check
      // is deferred to the cascade-aware pass (validateCascadeDrops) because the
      // aspect may be supplied by a structural ancestor, not the template (§4.9).
      result = result.filter((b) => !pathsEqual(b.path, entry.path));
      continue;
    }
    const idx = result.findIndex((b) => pathsEqual(b.path, entry.path));
    if (idx >= 0) {
      const baseEntry = result[idx]!;
      result[idx] = {
        ...baseEntry,
        ...entry,
        required: entry.value !== undefined ? undefined : entry.required ?? baseEntry.required,
      };
    } else {
      result.push(entry);
    }
  }
  return result;
}

function mergeSurfaces(
  base: readonly SurfaceDecl[],
  incoming: readonly SurfaceDecl[],
  diagnostics: Diagnostic[],
  ctx: { source?: SourceSpan },
  registry?: ResolvedTypeRegistry
): SurfaceDecl[] {
  const result = [...base];
  for (const f of incoming) {
    const idx = result.findIndex((b) => b.name === f.name);
    if (idx < 0) {
      if (f.override) {
        diagnostics.push(
          makeDiag(
            "error",
            f.source,
            "OVERRIDE_NOT_INHERITED",
            "resolver.overrideNotInheritedSurface",
            { name: f.name }
          )
        );
      }
      result.push({ ...f, body: f.body });
      continue;
    }
    const baseEntry = result[idx]!;
    if (f.override) {
      // Clean-slate replacement; do not merge bodies.
      result[idx] = { ...f, body: f.body };
    } else {
      // Refinement: kind must be same or compatible (semantic check
      // could be added later); merge bodies.
      const isSubtype =
        baseEntry.typeName === f.typeName ||
        (registry && isSubtypeOf(f.typeName, baseEntry.typeName, registry));
      if (!isSubtype) {
        diagnostics.push(
          makeDiag(
            "error",
            f.source,
            "TYPE_CHANGE_NEEDS_OVERRIDE",
            "resolver.typeChangeNeedsOverrideSurface",
            { name: f.name, from: baseEntry.typeName, to: f.typeName }
          )
        );
      }
      const fulfilledByContent = bodyHasContent(f.body);
      result[idx] = {
        ...baseEntry,
        ...f,
        // Narrow to the subtype kind when refining; otherwise keep base kind.
        typeName: isSubtype ? f.typeName : baseEntry.typeName,
        body: mergeBodies(baseEntry.body, f.body, diagnostics, ctx, registry),
        required: fulfilledByContent ? undefined : f.required ?? baseEntry.required,
      };
    }
  }
  return result;
}

function mergeInterfaces(
  base: readonly InterfaceDecl[],
  incoming: readonly InterfaceDecl[],
  diagnostics: Diagnostic[],
  ctx: { source?: SourceSpan },
  registry?: ResolvedTypeRegistry
): InterfaceDecl[] {
  const result = [...base];
  for (const i of incoming) {
    const idx = result.findIndex((b) => b.name === i.name);
    if (idx < 0) {
      if (i.override) {
        diagnostics.push(
          makeDiag(
            "error",
            i.source,
            "OVERRIDE_NOT_INHERITED",
            "resolver.overrideNotInheritedInterface",
            { name: i.name }
          )
        );
      }
      result.push(i);
      continue;
    }
    const baseEntry = result[idx]!;
    if (i.override) {
      result[idx] = i;
    } else {
      const isSubtype =
        baseEntry.typeName === i.typeName ||
        (registry && isSubtypeOf(i.typeName, baseEntry.typeName, registry));
      if (!isSubtype) {
        diagnostics.push(
          makeDiag(
            "error",
            i.source,
            "TYPE_CHANGE_NEEDS_OVERRIDE",
            "resolver.typeChangeNeedsOverrideInterface",
            { name: i.name, from: baseEntry.typeName, to: i.typeName }
          )
        );
      }
      const fulfilledByContent = interfaceBodyHasContent(i.body);
      result[idx] = {
        ...baseEntry,
        ...i,
        typeName: isSubtype ? i.typeName : baseEntry.typeName,
        body: mergeInterfaceBodies(baseEntry.body, i.body, diagnostics, ctx),
        required: fulfilledByContent ? undefined : i.required ?? baseEntry.required,
      };
    }
  }
  return result;
}

function interfaceBodyHasContent(body: InterfaceBody): boolean {
  return (
    body.fields.length > 0 ||
    body.aspects.length > 0 ||
    body.descriptions.length > 0
  );
}

function bodyHasContent(body: Body): boolean {
  return (
    body.fields.length > 0 ||
    body.aspects.length > 0 ||
    body.descriptions.length > 0 ||
    body.surfaces.length > 0 ||
    body.interfaces.length > 0 ||
    body.modules.length > 0
  );
}

function mergeModules(
  base: readonly ModuleDecl[],
  incoming: readonly ModuleDecl[],
  diagnostics: Diagnostic[],
  ctx: { source?: SourceSpan },
  registry?: ResolvedTypeRegistry
): ModuleDecl[] {
  const result = [...base];
  for (const m of incoming) {
    const idx = result.findIndex((b) => b.name === m.name);
    if (idx < 0) {
      if (m.override) {
        diagnostics.push(
          makeDiag(
            "error",
            m.source,
            "OVERRIDE_NOT_INHERITED",
            "resolver.overrideNotInheritedModule",
            { name: m.name }
          )
        );
      }
      result.push(m);
      continue;
    }
    const baseEntry = result[idx]!;
    if (m.override) {
      result[idx] = m;
    } else {
      const isSubtype =
        baseEntry.typeName === m.typeName ||
        (registry && isSubtypeOf(m.typeName, baseEntry.typeName, registry));
      if (!isSubtype) {
        diagnostics.push(
          makeDiag(
            "error",
            m.source,
            "TYPE_CHANGE_NEEDS_OVERRIDE",
            "resolver.typeChangeNeedsOverrideModule",
            { name: m.name, from: baseEntry.typeName, to: m.typeName }
          )
        );
      }
      const fulfilledByContent = bodyHasContent(m.body);
      result[idx] = {
        ...baseEntry,
        ...m,
        typeName: isSubtype ? m.typeName : baseEntry.typeName,
        body: mergeBodies(baseEntry.body, m.body, diagnostics, ctx, registry),
        required: fulfilledByContent ? undefined : m.required ?? baseEntry.required,
      };
    }
  }
  return result;
}

function mergeInterfaceBodies(
  base: InterfaceBody,
  incoming: InterfaceBody,
  diagnostics: Diagnostic[],
  _ctx: { source?: SourceSpan }
): InterfaceBody {
  // Apply drops on the base
  let fields = [...base.fields];
  let aspects = [...base.aspects];
  // Descriptions do NOT cascade (see mergeBodies) — keep only own.
  const descriptions = [...incoming.descriptions];

  for (const drop of incoming.drops) {
    if (drop.target.length === 1) {
      const head = drop.target[0]!;
      fields = fields.filter((f) => !(f.path.length === 1 && f.path[0] === head));
      aspects = aspects.filter((l) => !(l.path.length === 1 && l.path[0] === head));
    } else {
      fields = fields.filter((f) => !pathsEqual(f.path, drop.target));
      aspects = aspects.filter((l) => !pathsEqual(l.path, drop.target));
    }
  }

  fields = mergeFields(fields, incoming.fields, diagnostics);
  aspects = mergeAspects(aspects, incoming.aspects, diagnostics);

  return { fields, aspects, descriptions, drops: [] };
}

// =============================================================================
// Instance resolution
// =============================================================================

/**
 * v0.9.0 §1.2 — a partial-presence block's DECLARATIONS are ordinary members of
 * the module. Folding them into the body here (before template merge, so a
 * template/`in`-extension sees them like any other member) is what makes them
 * resolve, draw, and carry edges instead of being dropped.
 *
 * Submodules and processes are stamped born-on the block's plane — exactly what
 * an `on <plane> module X` prefix produces, so presence needs no special case
 * for them. Interfaces, surfaces and fields have no plane slot of their own, so
 * `computePresence` reads the block for those. The block stays on the body: it
 * is still the source of truth for member-level presence and for tooling.
 */
function foldPartialPresence(decl: ModuleDecl): ModuleDecl {
  const blocks = decl.body.partialOn;
  if (!blocks || blocks.length === 0) return decl;
  const body = decl.body;
  const fields = [...body.fields];
  const interfaces = [...body.interfaces];
  const surfaces = [...body.surfaces];
  const modules = [...body.modules];
  const processes = [...body.processes];
  const subprocesses = [...body.subprocesses];
  const bornOn = <T extends { onPlanes?: readonly QualifiedName[] }>(d: T, plane: QualifiedName): T =>
    d.onPlanes && d.onPlanes.length > 0 ? d : { ...d, onPlanes: [plane] };
  for (const pp of blocks) {
    fields.push(...pp.body.fields);
    interfaces.push(...pp.body.interfaces);
    surfaces.push(...pp.body.surfaces);
    for (const m of pp.body.modules) modules.push(bornOn(m, pp.plane));
    for (const p of pp.body.processes) processes.push(bornOn(p, pp.plane));
    subprocesses.push(...pp.body.subprocesses);
  }
  return { ...decl, body: { ...body, fields, interfaces, surfaces, modules, processes, subprocesses } };
}

export function resolveModule(
  declIn: ModuleDecl,
  registry: ResolvedTypeRegistry,
  diagnostics: Diagnostic[],
  space?: QualifiedName
): ResolvedModule {
  const decl = foldPartialPresence(declIn);
  const kind = lookupKind(registry, decl.typeName, space);
  let baseType: BaseType = "module";
  // Unknown kind falls back to the `module` base's stamped body so the
  // builtin widget defaults still apply.
  let template: Body = registry.kinds.get("module")?.stampedBody ?? EMPTY_BODY;
  if (!kind) {
    // Second arm (item 4): the name isn't declared or imported here, but the
    // stdlib publishes it as an exported kind — the likeliest cause is a missing
    // import, so point at the `use … from <pkg>` that would resolve it (first
    // publisher; the LSP quick-fix offers one action per package).
    const pkgs = STDLIB_KIND_PACKAGES.get(decl.typeName);
    const hasImport = !!(pkgs && pkgs.length > 0);
    diagnostics.push(
      makeDiag(
        "warning",
        decl.typeSource ?? decl.source,
        "UNKNOWN_TYPE",
        "resolver.unknownTypeModule",
        // `body` carries the literal empty-body syntax `{ ... }` as a param:
        // the catalog renderer doubles apostrophes for ICU, which can't then
        // emit literal braces — so a bare `{ ... }` in the template would be a
        // MALFORMED_ARGUMENT. Arch syntax isn't localized anyway.
        { typeName: decl.typeName, body: "{ ... }", hasImport, pkg: hasImport ? pkgs![0] : "" }
      )
    );
  } else {
    baseType = kind.baseType;
    template = kind.stampedBody;
  }

  let stamped = mergeBodies(template, decl.body, diagnostics, decl, registry);
  // Reclassify: parser only had heuristics, so any sub-decl whose kind's
  // base is surface/interface (per the registry) needs to move from the
  // modules array into the appropriate one.
  stamped = reclassifyByRegistry(stamped, registry, space);

  return {
    decl,
    name: decl.name,
    typeName: decl.typeName,
    baseType,
    id: decl.id,
    ...(decl.unnamed ? { unnamed: true } : {}),
    parentPath: decl.parentPath,
    body: stamped,
    surfaces: stamped.surfaces.map((f) => resolveSurface(f, registry, diagnostics, space)),
    interfaces: stamped.interfaces.map((i) => resolveInterface(i, registry, diagnostics, space)),
    modules: stamped.modules.map((m) => resolveModule(m, registry, diagnostics, space)),
    // v0.11 (D1a): identity space, from the declaring file; a placed
    // orphan's subtree is re-stamped to its landing space in applyInClauses.
    ...(space && space.length > 0 ? { space } : {}),
  };
}

/**
 * Reclassify body sub-declarations using the kind registry. The parser
 * uses heuristics (literal `surface` keyword + stdlib interface kinds);
 * user-defined surface/interface kinds end up in `modules` after parsing
 * and need to be moved into `surfaces` / `interfaces` based on their base.
 */
function reclassifyByRegistry(
  body: Body,
  registry: ResolvedTypeRegistry,
  space?: QualifiedName
): Body {
  const newSurfaces: SurfaceDecl[] = [...body.surfaces];
  const newInterfaces: InterfaceDecl[] = [...body.interfaces];
  const newModules: ModuleDecl[] = [];

  for (const m of body.modules) {
    const kind = lookupKind(registry, m.typeName, space);
    if (kind?.baseType === "surface") {
      // Convert to SurfaceDecl shape by dropping what a surface has no place
      // for and carrying the rest — including `unnamed`, so the modules-only
      // check (§4.14) sees a bare custom-typed surface the parser tentatively
      // classified as a module.
      //
      // Written as a subtraction on purpose. Listing the fields to KEEP is how
      // `decorations` went missing here (and `exported` on the interface arm):
      // a forgotten field is silent, and a dropped decoration reads as one the
      // author never wrote — no window, no marker on the axis, not even an
      // "unknown decoration" for an invented name. Subtracting means a field
      // added to `ModuleDecl` later arrives by default instead of vanishing.
      const {
        id: _id,
        onPlanes: _onPlanes,
        parentPath: _parentPath,
        exported: _exported,
        extentSource: _extentSource,
        idSource: _idSource,
        parentSource: _parentSource,
        ...surface
      } = m;
      newSurfaces.push(surface);
      continue;
    }
    if (kind?.baseType === "interface") {
      // Same subtraction, minus the body conversion — interfaces don't carry a
      // Body, just an InterfaceBody — and keeping `exported`/`extentSource`,
      // which an interface does have. `hasBody` rides along, so a bare
      // `kafka {}` default (§1.6) survives the module→interface move.
      const {
        id: _id,
        onPlanes: _onPlanes,
        parentPath: _parentPath,
        idSource: _idSource,
        parentSource: _parentSource,
        body,
        ...shared
      } = m;
      newInterfaces.push({ ...shared, body: bodyToInterfaceBody(body) });
      continue;
    }
    newModules.push(m);
  }

  return {
    ...body,
    surfaces: newSurfaces,
    interfaces: newInterfaces,
    modules: newModules,
  };
}

function bodyToInterfaceBody(body: Body): InterfaceBody {
  // Flatten module body into an interface body — interfaces don't have
  // nested decls, so any subdecls would be a structural error caught in
  // validation.
  return {
    fields: body.fields,
    aspects: body.aspects,
    descriptions: body.descriptions,
    drops: body.drops,
  };
}

function resolveSurface(
  decl: SurfaceDecl,
  registry: ResolvedTypeRegistry,
  diagnostics: Diagnostic[],
  space?: QualifiedName
): ResolvedSurface {
  const kind = lookupKind(registry, decl.typeName, space);
  let template: Body = EMPTY_BODY;
  if (!kind) {
    diagnostics.push(
      makeDiag(
        "error",
        decl.typeSource ?? decl.source,
        "UNKNOWN_TYPE",
        "resolver.unknownTypeSurface",
        { typeName: decl.typeName }
      )
    );
  } else if (kind.baseType !== "surface") {
    diagnostics.push(
      makeDiag(
        "error",
        decl.typeSource ?? decl.source,
        "WRONG_BASE_TYPE",
        "resolver.wrongBaseTypeSurface",
        { typeName: decl.typeName, baseType: kind.baseType }
      )
    );
  } else {
    template = kind.stampedBody;
  }

  let stamped = mergeBodies(template, decl.body, diagnostics, decl, registry);
  stamped = reclassifyByRegistry(stamped, registry, space);

  if (stamped.modules.length > 0) {
    diagnostics.push(
      makeDiag(
        "error",
        decl.source,
        "SURFACE_CONTAINS_MODULE",
        "resolver.surfaceContainsModule",
        { name: decl.name }
      )
    );
  }

  return {
    decl,
    name: decl.name,
    typeName: decl.typeName,
    body: stamped,
    surfaces: stamped.surfaces.map((f) => resolveSurface(f, registry, diagnostics, space)),
    interfaces: stamped.interfaces.map((i) => resolveInterface(i, registry, diagnostics, space)),
  };
}

function resolveInterface(
  decl: InterfaceDecl,
  registry: ResolvedTypeRegistry,
  diagnostics: Diagnostic[],
  space?: QualifiedName
): ResolvedInterface {
  const kind = lookupKind(registry, decl.typeName, space);
  if (!kind) {
    diagnostics.push(
      makeDiag(
        "error",
        decl.typeSource ?? decl.source,
        "UNKNOWN_TYPE",
        "resolver.unknownTypeInterface",
        { typeName: decl.typeName }
      )
    );
  } else if (kind.baseType !== "interface") {
    diagnostics.push(
      makeDiag(
        "error",
        decl.typeSource ?? decl.source,
        "WRONG_BASE_TYPE",
        "resolver.wrongBaseTypeInterface",
        { typeName: decl.typeName, baseType: kind.baseType }
      )
    );
  }

  // Layer the kind's stamped body under the instance body. Interface
  // kinds carry type-level `cascade widget.*` defaults (e.g. kafka →
  // dashed + envelope glyph, sse/websocket → dashed, rest/grpc → solid)
  // that must reach the resolved interface so edge styling and the
  // inspector see the inherited config. The instance's own fields win
  // over inherited ones via mergeInterfaceBodies.
  const template = kind ? bodyToInterfaceBody(kind.stampedBody) : EMPTY_INTERFACE_BODY;
  const instanceBody = decl.body ?? EMPTY_INTERFACE_BODY;
  const body = mergeInterfaceBodies(template, instanceBody, diagnostics, {
    source: decl.source,
  });
  return {
    decl,
    name: decl.name,
    typeName: decl.typeName,
    body,
  };
}

// =============================================================================
// Cascade resolution (mechanism B from spec §4.5)
// =============================================================================

/**
 * Walk the structural tree and return a copy of the model with cascade
 * field values inlined into each descendant's body.
 *
 * - `cascade` fields: descendants without their own value inherit the
 *   nearest ancestor's value (override semantics).
 * - `append` fields: descendants compose with ancestors. For list
 *   values, items concatenate; for path strings, segments join with `/`.
 *   Other types fall back to override behavior.
 *
 * Aspects always cascade with override semantics (see spec §4.4).
 */
// Incremental cascade (B3 stage 3). A module's cascaded BODY is a pure function
// of (its resolved value `m`, the inherited cascade context). We cache that body
// + the derived child-context per (m, ctx-object), keyed by identity: `m` is
// stable across rebuilds (resolve cache + identity-preserving tree assembly),
// and the child-context object is stored in the entry so unchanged subtrees are
// reached via the SAME ctx object → cache hit. An edit recomputes only changed
// modules' bodies; their ancestors are reassembled (O(depth)) to thread the new
// children through. The fully-assembled result is memoised too, so a no-op
// rebuild returns the identical object.
interface CascadeCtx {
  readonly fields: ReadonlyMap<string, FieldDecl>;
  readonly aspects: ReadonlyMap<string, AspectEntry>;
}
interface CascadeEntry {
  readonly newFields: FieldDecl[];
  readonly newAspects: AspectEntry[];
  readonly newSurfaces: readonly ResolvedSurface[];
  readonly newInterfaces: readonly ResolvedInterface[];
  readonly childCtx: CascadeCtx;
  result?: ResolvedModule;
}
// Keyed by the module's BODY, not the module object. `computeEntry` reads only
// `m.body` (fields + aspects), and identity-preserving tree assembly shares the
// body when only a node's CHILDREN change (path-copy spreads `{...node}`). So an
// ancestor that's rebuilt purely to thread a new child still hits this cache and
// returns the SAME `childCtx` object — its other children stay cached. Keying by
// `m` instead would regenerate `childCtx` and re-cascade every sibling.
const cascadeCache = new WeakMap<Body, Map<CascadeCtx, CascadeEntry>>();
const CASCADE_ROOT_CTX: CascadeCtx = { fields: new Map(), aspects: new Map() };
let cascadeHits = 0;
let cascadeMisses = 0;
export function _resetCascadeCache(): void {
  cascadeHits = 0;
  cascadeMisses = 0;
}
export function _cascadeCacheStats(): { hits: number; misses: number } {
  return { hits: cascadeHits, misses: cascadeMisses };
}

/** The body slice the structural cascade operates on — shared by module
 *  bodies, surface bodies, and (fields-only) interface bodies. */
interface CascadeBodyInput {
  readonly fields: readonly FieldDecl[];
  readonly aspects: readonly AspectEntry[];
  readonly cascadeDrops?: readonly CascadeDrop[];
}

/**
 * Resolve one body against the inherited cascade context (§4.5.B). Returns
 * the resolved field/aspect lists plus the context its structural children
 * (nested modules, surfaces, interfaces) inherit. `includeAspects: false`
 * runs the field walk only — surfaces/interfaces keep their own aspects
 * untouched (aspects on non-module nodes resolve lazily via owner chains).
 */
function cascadeBody(
  body: CascadeBodyInput,
  ctx: CascadeCtx,
  includeAspects: boolean
): { newFields: FieldDecl[]; newAspects: AspectEntry[]; childCtx: CascadeCtx } {
  const inheritedFields = ctx.fields;
  const inheritedAspects = ctx.aspects;

  // Cascade tombstones (§4.9 "drop is absolute"): a field/aspect dropped on
  // this node must not be re-inherited from a structural ancestor here, and
  // the chain is broken for descendants too. Field drops match by prefix so a
  // dropped cascade-group root collapses its sub-fields; aspects match exact.
  const fieldDrops = (body.cascadeDrops ?? []).filter((d) => d.kind === "field");
  const aspectDrops = (body.cascadeDrops ?? []).filter((d) => d.kind === "aspect");
  const fieldDropped = (p: QualifiedName): boolean =>
    fieldDrops.some((d) => pathStartsWith(p, d.target));
  const aspectDropped = (p: QualifiedName): boolean =>
    aspectDrops.some((d) => pathsEqual(p, d.target));

  // Cascade-group drop: when this instance redefines an inherited
  // group root with a different value, strip all inherited
  // sub-fields under that root before they're considered for the
  // cascade fall-through below.
  let effectiveInheritedFields = inheritedFields;
  // Paths where this instance redefines an inherited cascade-group root
  // with a DIFFERENT value. The root is an identity choice (e.g. the
  // `widget` tag), so the instance's own value wins outright — both its
  // inherited sub-fields (dropped below) AND the root itself (the §4.9
  // nested-wins fall-through in pass 1 is skipped for these paths). Without
  // this, a container's `cascade * widget: arch-module` would override a
  // child's own-type `widget: arch-<figure>` even though the child clearly
  // replaced the group root.
  const replacedGroupRoots = new Set<string>();
  for (const own of body.fields) {
    if (own.value === undefined) continue;
    const rootEntry = inheritedFields.get(own.path.join("."));
    if (!rootEntry || !rootEntry.group) continue;
    if (valuesEqual(rootEntry.value, own.value)) continue;
    replacedGroupRoots.add(own.path.join("."));
    // Group-root replaced — drop inherited sub-fields under the root.
    const filtered = new Map(effectiveInheritedFields);
    for (const [key, inherited] of filtered) {
      if (
        !pathsEqual(inherited.path, own.path) &&
        pathStartsWith(inherited.path, own.path)
      ) {
        filtered.delete(key);
      }
    }
    effectiveInheritedFields = filtered;
  }

  const newFields: FieldDecl[] = [];
  const seen = new Set<string>();

  // 1. Pass through own fields that have a value.
  for (const own of body.fields) {
    if (own.value === undefined) continue;
    const path = own.path.join(".");
    const inherited = effectiveInheritedFields.get(path);
    // §4.6: the propagation mode is fixed at the type that INTRODUCES the
    // field — when the inherited chain entry is cascade/append-typed, this
    // value composes/flows even though the local kind's template never
    // re-declared the modifier. Adopt the mode from the chain.
    const append = own.append || inherited?.append || undefined;
    const cascade = own.cascade || inherited?.cascade || undefined;
    // §4.9 nested-wins: a value reachable via both a type template default
    // (fromTemplate) and a structural ancestor cascade resolves to the
    // ancestor's — the template default is the weaker source.
    if (
      own.fromTemplate &&
      !append &&
      inherited &&
      inherited.value !== undefined &&
      !replacedGroupRoots.has(path)
    ) {
      newFields.push(stripSource(inherited));
      seen.add(path);
      continue;
    }
    if (inherited && append) {
      newFields.push({
        ...own,
        append: true,
        value: composeAppend(inherited.value, own.value) as typeof own.value,
      });
    } else if (append !== own.append || cascade !== own.cascade) {
      // Adopted mode survives onto the resolved field so the value keeps
      // flowing to descendants (the child context collects flagged fields).
      newFields.push({
        ...own,
        ...(append ? { append: true } : {}),
        ...(cascade ? { cascade: true } : {}),
      });
    } else {
      newFields.push(own);
    }
    seen.add(path);
  }

  // 2. Inherit cascade/append values for paths the instance didn't set —
  //    unless the path was dropped here (tombstone breaks the cascade).
  for (const [path, inherited] of effectiveInheritedFields) {
    if (seen.has(path)) continue;
    if (fieldDropped(inherited.path)) {
      seen.add(path);
      continue;
    }
    newFields.push(stripSource(inherited));
    seen.add(path);
  }

  // 3. Preserve any value-less own declarations (e.g., kind-template
  //    `cascade team`) that didn't get a value from anywhere — they're
  //    still part of the schema.
  for (const own of body.fields) {
    const path = own.path.join(".");
    if (seen.has(path)) continue;
    newFields.push(own);
    seen.add(path);
  }

  // Compute what cascades to children. Start from the group-filtered view so
  // a replaced group root's inherited sub-fields stay dropped for descendants
  // ("the override starts with a fresh group", §4.6.1). Tombstoned paths are
  // stripped so the chain stays broken for descendants (§4.9 — they do not
  // resume from a deeper ancestor).
  const childrenInheritedFields = new Map<string, FieldDecl>(effectiveInheritedFields);
  for (const f of newFields) {
    if (f.cascade || f.append) {
      childrenInheritedFields.set(f.path.join("."), f);
    }
  }
  for (const [key, f] of childrenInheritedFields) {
    if (fieldDropped(f.path)) childrenInheritedFields.delete(key);
  }

  if (!includeAspects) {
    // Fields-only walk: keep own aspects untouched; the aspect context
    // passes through unchanged for nested surfaces.
    return {
      newFields,
      newAspects: [...body.aspects],
      childCtx: { fields: childrenInheritedFields, aspects: inheritedAspects },
    };
  }

  const ownAspects = new Map<string, AspectEntry>();
  for (const l of body.aspects) ownAspects.set(l.path.join("."), l);

  const newAspects: AspectEntry[] = [];
  for (const [path, inherited] of inheritedAspects) {
    if (ownAspects.has(path)) continue;
    if (aspectDropped(inherited.path)) continue; // tombstone breaks aspect cascade
    newAspects.push(stripAspectSource(inherited));
  }
  for (const own of body.aspects) newAspects.push(own);

  const childrenInheritedAspects = new Map<string, AspectEntry>(inheritedAspects);
  for (const l of newAspects) {
    childrenInheritedAspects.set(l.path.join("."), l);
  }
  for (const [key, l] of childrenInheritedAspects) {
    if (aspectDropped(l.path)) childrenInheritedAspects.delete(key);
  }

  return {
    newFields,
    newAspects,
    childCtx: { fields: childrenInheritedFields, aspects: childrenInheritedAspects },
  };
}

function sameFields(a: readonly FieldDecl[], b: readonly FieldDecl[]): boolean {
  return a.length === b.length && a.every((f, i) => f === b[i]);
}

/** Cascade fields into a surface and, recursively, its nested surfaces and
 *  interfaces (§4.5.B: … → surface → interface). Identity-preserving: an
 *  untouched subtree returns the same object. */
function cascadeSurface(s: ResolvedSurface, ctx: CascadeCtx): ResolvedSurface {
  const bc = cascadeBody(s.body, ctx, false);
  const surfaces = s.surfaces.map((c) => cascadeSurface(c, bc.childCtx));
  const interfaces = s.interfaces.map((i) => cascadeInterface(i, bc.childCtx));
  if (
    sameFields(bc.newFields, s.body.fields) &&
    surfaces.every((c, i) => c === s.surfaces[i]) &&
    interfaces.every((c, i) => c === s.interfaces[i])
  ) {
    return s;
  }
  return {
    ...s,
    body: { ...s.body, fields: bc.newFields },
    surfaces,
    interfaces,
  };
}

/** Cascade fields into an interface — the leaf of the §4.5.B walk. */
function cascadeInterface(i: ResolvedInterface, ctx: CascadeCtx): ResolvedInterface {
  const bc = cascadeBody({ fields: i.body.fields, aspects: i.body.aspects }, ctx, false);
  if (sameFields(bc.newFields, i.body.fields)) return i;
  return { ...i, body: { ...i.body, fields: bc.newFields } };
}

/**
 * §4.5 field/aspect cascade, then §4.3 field-expression resolution.
 *
 * The two belong together and in this order: an expression must be evaluated on
 * the node it CASCADED ONTO (so `cascade widget.color: colorize(@@team)` colours
 * each descendant by its own team), and no consumer should ever have to know
 * that field values were once expressions. `diagnostics`, when supplied,
 * collects what could not be resolved (a missing getter, a mistyped colour); the
 * render-only callers omit it.
 */
export function applyCascade(model: ResolvedModel, diagnostics?: Diagnostic[]): ResolvedModel {
  function computeEntry(m: ResolvedModule, ctx: CascadeCtx): CascadeEntry {
    const bc = cascadeBody(m.body, ctx, true);
    // §4.5.B: FIELDS are a separate namespace on facets. A module's fields carry
    // (with mode) to its child MODULES, but do NOT cross into its own surfaces /
    // interfaces at all — a facet neither inherits nor overrides a module field;
    // it declares its own, which then cascade DEEPER within the facet subtree
    // (surface → surface → interface) per that facet type's own cascade/append.
    // This is why a node `widget` (arch-module) no longer leaks onto every edge.
    // Aspects DO still cross (ownership/domain), so pass the aspects on but an
    // empty field context.
    const facetCtx: CascadeCtx = { fields: CASCADE_ROOT_CTX.fields, aspects: bc.childCtx.aspects };
    const newSurfaces = m.surfaces.map((s) => cascadeSurface(s, facetCtx));
    const newInterfaces = m.interfaces.map((i) => cascadeInterface(i, facetCtx));
    return {
      newFields: bc.newFields,
      newAspects: bc.newAspects,
      newSurfaces,
      newInterfaces,
      childCtx: bc.childCtx,
    };
  }

  function visit(m: ResolvedModule, ctx: CascadeCtx): ResolvedModule {
    let perM = cascadeCache.get(m.body);
    let entry = perM?.get(ctx);
    if (entry) {
      cascadeHits++;
    } else {
      cascadeMisses++;
      entry = computeEntry(m, ctx);
      if (!perM) {
        perM = new Map();
        cascadeCache.set(m.body, perM);
      }
      perM.set(ctx, entry);
    }
    const newChildren = m.modules.map((c) => visit(c, entry!.childCtx));
    // Reuse the fully-assembled result when nothing below changed.
    const prev = entry.result;
    if (
      prev &&
      prev.modules.length === newChildren.length &&
      newChildren.every((c, i) => c === prev.modules[i])
    ) {
      return prev;
    }
    const result: ResolvedModule = {
      ...m,
      body: { ...m.body, fields: entry.newFields, aspects: entry.newAspects },
      surfaces: entry.newSurfaces,
      interfaces: entry.newInterfaces,
      modules: newChildren,
    };
    entry.result = result;
    return result;
  }

  const cascadedModules = model.modules.map((m) => visit(m, CASCADE_ROOT_CTX));

  // v0.10 §5.1: resolve PROCESS aspects. A module-scoped process inherits its
  // owner module's cascaded aspects; its own declaration overrides by path
  // (parity with module→child cascade — override-only, all aspects cascade).
  // Free-standing processes (no owner module) keep only their own aspects.
  let processModels = model.processModels;
  if (processModels && processModels.length > 0) {
    const ownerAspects = new Map<string, readonly AspectEntry[]>();
    const indexModule = (m: ResolvedModule, parent: readonly string[]): void => {
      const path = [...parent, m.name];
      ownerAspects.set(path.join("."), m.body.aspects);
      for (const c of m.modules) indexModule(c, path);
    };
    for (const m of cascadedModules) indexModule(m, []);
    processModels = processModels.map((pm) => {
      const inherited = pm.ownerPath ? ownerAspects.get(pm.ownerPath.join(".")) : undefined;
      if (!inherited || inherited.length === 0) return pm;
      const byPath = new Map<string, AspectEntry>();
      for (const a of inherited) byPath.set(a.path.join("."), a);
      for (const a of pm.aspects ?? []) byPath.set(a.path.join("."), a); // own overrides
      return { ...pm, aspects: [...byPath.values()] };
    });
  }

  return resolveFieldExprs({ ...model, modules: cascadedModules, processModels }, diagnostics);
}

function stripSource(f: FieldDecl): FieldDecl {
  return { ...f, source: undefined, pathSource: undefined };
}

function stripAspectSource(l: AspectEntry): AspectEntry {
  return { ...l, source: undefined, pathSource: undefined };
}

function composeAppend(parent: unknown, child: unknown): unknown {
  const parentObj = parent as { kind?: string; items?: unknown[]; text?: string } | undefined;
  const childObj = child as { kind?: string; items?: unknown[]; text?: string } | undefined;

  // List + list → concat
  if (parentObj?.kind === "list" && Array.isArray(parentObj.items)) {
    const childItems = childObj?.kind === "list" && Array.isArray(childObj.items)
      ? childObj.items
      : child !== undefined ? [child] : [];
    return {
      ...parentObj,
      items: [...parentObj.items, ...childItems],
    };
  }
  // Scalar parent + list child → the parent item leads the composed list.
  // A bare single value (`tags: pii`) is always a scalar — it must compose
  // with a child's list exactly like the bracket form `[pii]` would.
  if (childObj?.kind === "list" && Array.isArray(childObj.items) && parent !== undefined) {
    return { ...childObj, items: [parent, ...childObj.items] };
  }
  // String / path → join with "/"
  const parentText = parentObj?.text;
  const childText = childObj?.text;
  if (typeof parentText === "string" && typeof childText === "string") {
    const sep = parentText.endsWith("/") || childText.startsWith("/") ? "" : "/";
    return { ...(childObj ?? {}), text: `${parentText}${sep}${childText}` };
  }
  // Fallback: child wins (override semantics).
  return child !== undefined ? child : parent;
}

// =============================================================================
// Validation: one-op-per-target
// =============================================================================

function validateOneOpPerTarget(body: Body, diagnostics: Diagnostic[]): void {
  // Unified keying: a body member operates on a "target" identified by its
  // qualified path or name. Sub-declarations use plain names (no prefix);
  // aspects are namespaced under "aspects.*". Drops use the literal target
  // path. So `drop metrics` and `component metrics` collide on key
  // "metrics"; `drop metrics.Emit` is its own key.
  const seen = new Map<string, SourceSpan | undefined>();
  const firstFlagged = new Set<string>();

  const seeAt = (key: string, span: SourceSpan | undefined) => {
    if (seen.has(key)) {
      diagnostics.push(makeDiag("error", span, "MULTI_OP_ON_TARGET", "resolver.multiOpOnTarget", { key }));
      // Also flag the FIRST occurrence once — for a cross-file extension conflict
      // both sites must surface, not only the later one (§4.16).
      const first = seen.get(key);
      if (first && !firstFlagged.has(key)) {
        diagnostics.push(makeDiag("error", first, "MULTI_OP_ON_TARGET", "resolver.multiOpOnTarget", { key }));
        firstFlagged.add(key);
      }
      return;
    }
    seen.set(key, span);
  };

  for (const f of body.fields) seeAt(f.path.join("."), f.source);
  for (const l of body.aspects) seeAt(`aspects.${l.path.join(".")}`, l.source);
  for (const fa of body.surfaces) seeAt(fa.name, fa.source);
  for (const it of body.interfaces) seeAt(it.name, it.source);
  for (const m of body.modules) seeAt(m.name, m.source);
  for (const d of body.drops) seeAt(d.target.join("."), d.source);
}

// =============================================================================
// Helpers
// =============================================================================

function pathsEqual(a: readonly Name[], b: readonly Name[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function pathStartsWith(path: readonly Name[], prefix: readonly Name[]): boolean {
  if (path.length < prefix.length) return false;
  for (let i = 0; i < prefix.length; i++) if (path[i] !== prefix[i]) return false;
  return true;
}

/** Shallow value equality — enough to decide "did the author write
 * the same literal here?" for cascade-group drop semantics. Doesn't
 * compare source positions. */
function valuesEqual(a: Value | undefined, b: Value | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === "string" || a.kind === "identifier") {
    return (a as { text: string }).text === (b as { text: string }).text;
  }
  if (a.kind === "number") return (a as { value: number }).value === (b as { value: number }).value;
  if (a.kind === "boolean") return (a as { value: boolean }).value === (b as { value: boolean }).value;
  if (a.kind === "list") {
    const ai = (a as { items: readonly Value[] }).items;
    const bi = (b as { items: readonly Value[] }).items;
    if (ai.length !== bi.length) return false;
    return ai.every((v, i) => valuesEqual(v, bi[i]));
  }
  return false;
}
